import { listPrayerAttempts, readTestimonyDraftSlot } from '@/lib/content-lifecycle/store';
import { PrayerAuthoringSession, prayerEditorAuth, prayerEditorIsCurrent } from '@/lib/prayer-authoring';
import {
  useCallback,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
  type CSSProperties,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAdiaStore } from '@/lib/store';
import {
  deletePrayerProtected,
  restorePrayerProtected,
} from '@/lib/keepsake/prayer-actions';
import { consumeJobLocator, peekJobLocator } from '@/lib/keepsake/job-locator';
import { getAuthUserId, isAuthResolved } from '@/lib/keepsake/epochs';
import {
  fetchKeepsakeDelivery,
  pollKeepsakeJobStatus,
  type KeepsakePreviewBindingV1,
} from '@/lib/keepsake/client';
import { readKeepsakeGate } from '@/lib/keepsake/fences';
import { getAuthEpoch } from '@/lib/keepsake/epochs';
import { deliverKeepsake } from '@/lib/export-prayer';

// Minimal status/delivery binding for the job-locator flow: owner + auth
// epoch + durable local epoch captured now (no server preview involved;
// status and delivery are epoch-checked exactly like the sheet's flow).
async function beginKeepsakePreviewBindingForLocator(): Promise<KeepsakePreviewBindingV1 | null> {
  const ownerId = getAuthUserId();
  if (!ownerId) return null;
  const gate = await readKeepsakeGate();
  if (gate.hold.held) return null;
  return {
    previewVersion: 1,
    previewId: crypto.randomUUID(),
    exportAttemptId: crypto.randomUUID(),
    expectedOwner: ownerId,
    authEpoch: getAuthEpoch(),
    localPrayerEpoch: gate.localPrayerEpoch,
    identityMode: 'cr-baseline-v1',
    createdAt: new Date().toISOString(),
    serverPreview: null,
  };
}
import { useVoiceCapture } from '@/hooks/useVoiceCapture';
import { useNavLayer, useNavStack } from '@/hooks/useNavLayer';
import { getContentOwnerBinding } from '@/lib/content-owner';
import {
  abandonTestimonyOpenRequest,
  claimTestimonyOpenRequest,
  consumeTestimonyOpenRequest,
  testimonyOpenRequestPending,
} from '@/lib/testimony-open-request';
import { useSharePrivacyGate } from '@/hooks/useSharePrivacyGate';
import { useSaveCue } from '@/hooks/useSaveCue';
import { useSettingsStore } from '@/lib/settings-store';
import { useSyncStore } from '@/lib/sync-store';
import { getOnline } from '@/lib/online-store';
import { checkDiscoveryMoment, markDiscoveryShown } from '@/lib/discovery';
import Garden, { PersonView } from '@/components/Garden';
import RememberStream from '@/components/RememberStream';
import EvidenceBook from '@/components/EvidenceBook';
import PersonKeepsakeSheet from '@/components/PersonKeepsakeSheet';
import PrayerDetail from '@/components/PrayerDetail/PrayerDetail';
import LetThemKnowSheet from '@/components/LetThemKnowSheet';
import MailCardSheet from '@/components/MailCardSheet/MailCardSheet';
import ChoiceSheet from '@/components/ChoiceSheet/ChoiceSheet';
import ShareConfirmDialog from '@/components/ShareConfirmDialog';
import { PICKER_ENABLED, BIBLE_ENABLED } from '@/lib/feature-flags';
import ClearButton from '@/components/ClearButton';
import MicButton from '@/components/MicButton';
import HeroWaveform from '@/components/HeroWaveform';
import HomePrayerScene from '@/components/HomePrayerScene';
import PraiseOrPrayGate, { EntryModeChoice, type ComposeMode } from '@/components/PraiseOrPrayGate';
import { topmostModal } from '@/hooks/useDialogFocus';
import GatheringIndicator from '@/components/GatheringIndicator';
import ResumeRecordingCard from '@/components/ResumeRecordingCard';
import PendingTranscriptionPill from '@/components/PendingTranscriptionPill';
import OfflineIndicator from '@/components/OfflineIndicator';
import InlineSaveRescueBanner, { type RescueDiscardOutcome } from '@/components/InlineSaveRescueBanner';
import DeletionNotices from '@/components/DeletionNotices/DeletionNotices';
import HomeFeedbackArea from '@/components/HomeFeedbackArea/HomeFeedbackArea';
import {
  getNoticeStackGeometry,
  setNoticeStackCap,
  subscribeNoticeStackGeometry,
} from '@/lib/app-notice-stack';
import type {
  ProtectionNoticeInput,
  UndoNoticeInput,
} from '@/components/DeletionNotices/notice-controller';
import {
  captureNoticeSource,
  type NoticeSource,
} from '@/components/DeletionNotices/notice-host';
import MaintenanceBanner from '@/components/MaintenanceBanner';
import ContentLifecycleNotice from '@/components/ContentLifecycleNotice';
import {
  CoreReadOnlyError,
  KEEPSAKES_ENABLED,
  enhancedVoiceAllowed,
  isCoreReadOnly,
} from '@/lib/core-cutover';
import type { Prayer, ScriptureAnchor } from '@/types/prayer';
import {
  PENDING_PRAYER_ALREADY_SAVED,
  collectInlineAudioCommitClaims,
  findHeldRecordingConflicts,
  saveRefusedForUnreadableRecording,
  createPendingPrayerFromAudio,
  finalizeCommittedAudio,
  publishCommittedInlineAudio,
  refreshInlineAudioMirror,
  getInlineAudioDraftRecovery,
  homeInlineSaveOffer,
  inlineSaveOfferHeardNoWords,
  inlineSaveOfferFailedWithoutWords,
} from '@/lib/audio-queue';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { formatShortDateTime, isPraise } from '@/lib/prayer-utils';
import { mergeRecoveredVoiceText } from '@/lib/audio-recovery';
import {
  loadPrayerDraft,
  loadPrayerDraftAudioIds,
  persistPrayerDraft,
  persistPrayerDraftAudioIds,
} from '@/lib/prayer-draft';
import { clearBibleIndexSnapshot } from '@/lib/bible-index-view-store';
import { parseScriptureAnchor } from '@/lib/scripture-anchor';
import { syncContentLifecycle } from '@/lib/content-lifecycle/sync';
import styles from './PrayPage.module.css';
import { useUpdateHold } from '@/hooks/useUpdateHold';
import { holdUpdate, registerDraftFlusher } from '@/lib/update-safety';

function entrySeed(mode: ComposeMode, createdAt: string): Pick<Prayer, 'parsed' | 'status' | 'answeredAt' | 'userEditedFields'> {
  return mode === 'praise' ? {
    parsed: { who: 'Self', core_prayer: '', suggestedStatus: null, mentionedPeople: [], cardLabel: null },
    status: 'Answered', answeredAt: createdAt, userEditedFields: { who: true, status: true },
  } : { parsed: null, status: 'Open' };
}

type SaveStatus = null | 'saved' | 'savedOffline' | 'failed';

// How long the saved-confirm dwells before auto-clearing. The offline variant
// carries longer copy, so it stays ~2s longer to stay readable (its CSS
// animation-duration is stretched to match so it doesn't fade out early).
const SAVED_CONFIRM_MS = 2200;
const SAVED_OFFLINE_CONFIRM_MS = 4200;
// A soft keyboard moves the visual viewport in steps while it animates. Home
// commits its keyboard geometry once the viewport has been still this long:
// about twice the 50 to 60 ms step cadence of the stepped keyboard stand-ins,
// and well inside the 800 ms the NA keyboard cases wait before measuring.
const KEYBOARD_SETTLE_MS = 120;
// The start-up hold's fail-safe, a 0.6 s wait and then a 0.24 s fade (0.16 s
// under reduced motion; PrayPage.module.css, entryWelcomeExpected). Once it
// has run, start-up is over. Keep it in step with that CSS.
const WELCOME_EXPECTED_FAIL_SAFE_MS = 840;

// Draft-only: protects in-progress text across navigation and reloads.
// A prayer is only committed to IndexedDB via the Save Prayer button (handleSave).
// "Has the user ever saved a prayer?" marker used by the inline
// pre-React routing script in index.html. When a marketing page is
// eventually added at "/", visitors with this flag skip straight to the
// prayer surface instead of seeing the marketing interstitial. Set once
// on first successful save; never cleared.
const VISITED_KEY = 'adia-visited';

interface PrayPageProps {
  /** One intentional action from the now-verified saved-prayers view. */
  initialPresentation?: {
    ownerId: string;
    authEpoch: number;
    prayerId?: string;
    focusWrite: boolean;
  };
}

export default function PrayPage({ initialPresentation }: PrayPageProps = {}) {
  const initialPresentationRef = useRef(initialPresentation);
  const { t, i18n } = useTranslation();
  const nav = useNavStack();
  const [text, setTextState] = useState('');
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null);
  const [gateLeaving, setGateLeaving] = useState(false);
  const [gatePresented, setGatePresented] = useState(false);
  const [capturedMode, setCapturedMode] = useState<ComposeMode | null>(null);
  const [modeRequired, setModeRequired] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  // The start-up hold (welcomeExpected) belongs to this mount's first entry:
  // once Home has been entry-ready, or the hold's fail-safe has run, later
  // corpus reloads never hold again.
  const [startupSettled, setStartupSettled] = useState(false);
  // Set the first time the start-up hold appears; starts the fail-safe clock.
  const [startupHoldSeen, setStartupHoldSeen] = useState(false);
  const [gateBypassed, setGateBypassed] = useState(() => Boolean(loadPrayerDraft() || loadPrayerDraftAudioIds().length));
  const [recoveryModes, setRecoveryModes] = useState<Record<string, ComposeMode>>({});
  const modeChoiceRef = useRef<HTMLDivElement>(null);
  const entryModeRef = useRef<HTMLDivElement>(null);
  const [entryModeBottom, setEntryModeBottom] = useState(0);
  const [guidanceElement, setGuidanceElement] = useState<HTMLDivElement | null>(null);
  const [guidanceHeight, setGuidanceHeight] = useState(0);
  // composeMode: the heading's words follow the chosen type (the Praise line
  // wraps to two lines at 390 px), so measure in the commit that changes them,
  // inside the tap's own batch, not one Home render later from the observer.
  useLayoutEffect(() => {
    if (!guidanceElement) { setGuidanceHeight(0); return; }
    const measure = () => setGuidanceHeight(Math.ceil(guidanceElement.getBoundingClientRect().height));
    const observer = new ResizeObserver(measure);
    observer.observe(guidanceElement);
    measure();
    return () => observer.disconnect();
  }, [guidanceElement, composeMode]);
  const recoveryChoiceRef = useRef<HTMLDivElement>(null);
  const [requiredRecoveryId, setRequiredRecoveryId] = useState<string | null>(null);
  const chooseMode = (mode: ComposeMode) => {
    setComposeMode(mode); setModeRequired(false); setGateBypassed(true);
  };
  const finishGateTransition = useCallback(() => setGateLeaving(false), []);
  useEffect(() => {
    if (!gateLeaving) return;
    const timer = window.setTimeout(finishGateTransition, 420);
    return () => window.clearTimeout(timer);
  }, [gateLeaving, finishGateTransition]);
  const requestMode = () => {
    setModeRequired(true);
    modeChoiceRef.current?.focus();
  };
  const textRef = useRef('');
  // Wrapped setter that keeps textRef in lockstep with state. The ref lets
  // async flows (enhanced-voice transcription delivering text via onFinal
  // inside an `await voice.stop()`) read the freshest value after React
  // has scheduled — but not yet applied — the state update.
  const setText = (value: string | ((prev: string) => string)) => {
    const next = typeof value === 'function' ? value(textRef.current) : value;
    textRef.current = next;
    setTextState(next);
  };
  const [isSaving, setIsSaving] = useState(false);
  // App-update safety: never reload the app while a prayer save is in flight.
  useUpdateHold(isSaving, 'prayer-save');
  // "Continue saving this {{mode}}." (entry.pendingMode) explains a mode frozen
  // by a Save that did not finish. An ordinary Save freezes the mode only while
  // it is in flight, and this line sits in flow under the mode row: shown then,
  // it pushed the words down 29 px and back as the Save finished, and the
  // guidance reappeared at the stale offset before jumping up. So it shows once
  // no Save is in flight, and once shown it stays through a retry's Save (no
  // shift at that tap). Presentation only (adjust-state-on-change, in render).
  const [pendingModeShown, setPendingModeShown] = useState(false);
  const showPendingMode = capturedMode !== null && (!isSaving || pendingModeShown);
  if (showPendingMode !== pendingModeShown) setPendingModeShown(showPendingMode);
  const [status, setStatus] = useState<SaveStatus>(null);
  // LC7/LC8/LC10 (reviews D-B, LC3 and LC10 item 3): a Save refused because a
  // recording it claims cannot be read in this page (`words` with typed words,
  // `recording` with none). Trying again here cannot succeed, so this is a
  // persistent alert in the feedback area, separate from the timed `status`
  // confirmations: announced (role="alert"), never cleared by a timer or a
  // fade, and cleared when the person acts (types, Clear, any new Save, a
  // Discard that settled). Set only at those two refusals.
  const [saveReopen, setSaveReopen] = useState<'words' | 'recording' | null>(null);
  // DS (duplicate-save follow-through, 2026-10-20): a typed Save that saved
  // nothing because recordings whose words this composer holds already left it
  // (findHeldRecordingConflicts): `saved` when proven saved as a prayer,
  // `unknown` when that cannot be proven (the recording is gone). Persistent
  // and announced like saveReopen; cleared when the person acts. Its button
  // "Save as a separate prayer" is the explicit choice to keep the words as
  // another prayer (handleSaveSeparate).
  const [heldConflict, setHeldConflict] = useState<{ kind: 'saved' | 'unknown'; ids: string[] } | null>(null);
  const heldConflictRef = useRef<HTMLParagraphElement>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // One-shot request for the detail to open its testimony dialog (Settings
  // Open). Cleared when the detail uses it or closes.
  const [detailInitialTestimony, setDetailInitialTestimony] =
    useState<{ id: string; prayerId: string } | null>(null);
  const [gardenOpen, setGardenOpen] = useState(false);
  // A3 (rule #78): person profile is its OWN PrayPage overlay (extracted from
  // Garden). `personFor` drives the `person` layer (uniform push from Garden
  // vine/grid, PrayerDetail who-name, EvidenceBook person-name; back -> opener).
  // `personDetailId` drives the DISTINCT `person-detail` layer (a second
  // PrayerDetail opened from the person timeline, so it never collides with a
  // top-level `detail` sitting underneath `person`). README §3.4.
  const [personFor, setPersonFor] = useState<string | null>(null);
  const [personDetailId, setPersonDetailId] = useState<string | null>(null);
  // Person keepsake preview — the destination for the Person profile's
  // keepsake CTA. PrayPage owns it so it overlays Garden (which stays
  // open underneath) and closes independently. Per unified-chrome
  // invariant, all overlay state is PrayPage's responsibility.
  const [keepsakeFor, setKeepsakeFor] = useState<string | null>(null);
  // Vine airplane / Remember share → LetThemKnowSheet. Garden still offers
  // its immediate held-close explanation, while PrayPage also applies the
  // authoritative live-store gate so a same-tick sync flip fails closed.
  const [shareCardForId, setShareCardForId] = useState<string | null>(null);
  // Flag-gated MailCardSheet overlay (Phase 2A — replaces Picker mount
  // per docs/plans/send-a-card-phase-2a-foundation-2026-05-27.md § 5.2).
  // Garden's onShareCard + RememberStream routes here when PICKER_ENABLED,
  // otherwise to shareCardForId. Entry and final-action privacy are both
  // revalidated here; Garden's toast remains an earlier UX guard.
  // PICKER_ENABLED flag name retained
  // for 2A (banked rename to MAILCARD_ENABLED at Phase 5 alongside
  // Picker.tsx deletion).
  const [mailCardForId, setMailCardForId] = useState<string | null>(null);
  // Flag-gated choice sheet between Garden's airplane share and the
  // destination sheet (M.3.7.b.1.a). Only set when PICKER_ENABLED is
  // true; flag-off path bypasses this slot entirely and sets
  // shareCardForId directly. Independent from shareCardForId and
  // mailCardForId so the three surfaces never collide.
  const [sendChoiceCardForId, setSendChoiceCardForId] = useState<string | null>(null);
  // Live-store privacy/version gate shared by Garden + Remember share entry.
  // Entry confirmation stays beneath the stepped flow; a distinct outbound
  // request can sit above Choice/Text/Mail when sync flips privacy mid-flow.
  const {
    authorize: authorizeShare,
    confirm: confirmShare,
    entryRequest: shareEntryRequest,
    outboundRequest: shareOutboundRequest,
    clearEntryRequest: clearShareEntryRequest,
    clearOutboundRequest: clearShareOutboundRequest,
    clearApprovedGrant: clearShareApprovedGrant,
  } = useSharePrivacyGate();
  const shareConfirmCardForId = shareEntryRequest?.prayerId ?? null;
  const shareReconfirmCardForId = shareOutboundRequest?.prayerId ?? null;
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [discovery, setDiscovery] = useState<{ who: string; months: number; prayerId: string } | null>(null);
  // Last-deleted prayer snapshot + timer for the Undo toast. Latest-wins:
  // a second delete during the toast window replaces the snapshot, which
  // matches iOS Mail's Undo behavior. Cleared on timer expiry (7s) or Undo
  // tap. Data is held in memory only — a reload cancels the undo window
  // but the hard local delete already happened.
  const [recentlyDeleted, setRecentlyDeleted] = useState<Prayer | null>(null);
  // S0 protection messages (locked keepsake.* copy), in two channels since the
  // 2026-10-15 amendment. `infoNotice`: protection pending / keepsake
  // preparing, 6 s expiry, tap dismisses. `failureNotice`: failed delete or
  // restore, policy-unavailable, account-changed, invalidated or failed
  // keepsake; no expiry, explicit Dismiss, a newer failure replaces it, an
  // info message never clears it, and only its own confirmed successful retry
  // (`origin`) does. No queue and no history.
  const [infoNotice, setInfoNotice] = useState<ProtectionNoticeInput | null>(null);
  const [failureNotice, setFailureNotice] = useState<(ProtectionNoticeInput & { origin: string }) | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Presentation metadata for DeletionNotices (P1, 2026-10-15): which offer of
  // a notice this is and, for a deletion, the dialogs that must unmount before
  // the notice chooses its host surface. Snapshot/message state stays above.
  const noticeEventRef = useRef(0);
  const [undoMeta, setUndoMeta] = useState<UndoNoticeInput>({ event: 0, source: null });
  // Mirrors DeletionNotices' pause report (hover, focus, or covered host). While
  // any reason applies the 7 s timer is not running.
  const undoPausedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chooseGateMode = (mode: ComposeMode) => {
    // Commit intent synchronously inside the tap: the composer is live at once
    // (the welcome only fades over it) and the field is focused within the
    // user's own gesture, so neither typing nor the keyboard waits for the
    // transition. The fade never owns or delivers the selected type.
    flushSync(() => { chooseMode(mode); setGateLeaving(true); });
    textareaRef.current?.focus({ preventScroll: true });
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const clearAreaRef = useRef<HTMLDivElement>(null);
  const [clearConfirmationTop, setClearConfirmationTop] = useState<number | null>(null);
  const upperViewportRef = useRef<HTMLDivElement>(null);
  const upperContentRef = useRef<HTMLDivElement>(null);
  const actionsBandRef = useRef<HTMLDivElement>(null);
  const homeActionStackRef = useRef<HTMLDivElement>(null);
  const [composerLayout, setComposerLayout] = useState({
    active: false, top: 0, height: 0, wholePage: false,
    feedbackTop: 0, feedbackBottom: 0,
  });
  const restingComposerHeightRef = useRef<number | null>(null);
  const topStatusRef = useRef<HTMLDivElement>(null);
  // Dock band ref drives the Clear pill clamp: the pill is pinned to the
  // bottom-right slot and must never land inside the dock zone (mic + label +
  // save) when the keyboard is closed. Reading offsetHeight at clamp time picks
  // up the live height including safe-area-inset-bottom on iPhone 14+ devices.
  const dockRef = useRef<HTMLDivElement>(null);
  // HomeFeedbackArea slots DeletionNotices portals into on home (callback refs
  // so the elements reach DeletionNotices as props, never read during render).
  const [undoSlot, setUndoSlot] = useState<HTMLDivElement | null>(null);
  const [headSlot, setHeadSlot] = useState<HTMLDivElement | null>(null);
  const [tailSlot, setTailSlot] = useState<HTMLDivElement | null>(null);
  // Keyboard inset over 100 px (the Clear pill's rule #11 threshold): the dock
  // is then below the keyboard and the feedback area is bounded by the
  // keyboard instead.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [clearTop, setClearTop] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const { prayers, addPrayer, loadPrayers, hydration } = useAdiaStore();

  const voice = useVoiceCapture();
  const registerVoiceTarget = voice.registerTarget;
  const cancelVoice = voice.cancel;
  const committedInlineAudioIdsRef = useRef(new Set<string>());
  // DS (2026-10-20): the words this composer received for each held recording
  // (memory only), so a Save can still tell whether its text carries them when
  // the recording's row is gone (findHeldRecordingConflicts).
  const heldWordsRef = useRef(new Map<string, string>());
  /** Drop recordings from the held set; the shared draft-ids key is written only when this page's own set changed. */
  const releaseHeldRecordings = (queueItemIds: Iterable<string>) => {
    let changed = false;
    for (const queueItemId of queueItemIds) {
      heldWordsRef.current.delete(queueItemId);
      if (committedInlineAudioIdsRef.current.delete(queueItemId)) changed = true;
    }
    if (changed) persistPrayerDraftAudioIds(committedInlineAudioIdsRef.current);
  };
  // RU item 2 (2026-10-20): the Bible passage of the recording Home offers,
  // named by the rescue banner (null for a recording with no passage).
  const audioHydrated = useAudioQueueStore(s => s.hydrated);
  const pendingAudioCount = useAudioQueueStore(s => s.pendingCount);
  const homeOfferId = useAudioQueueStore(s => homeInlineSaveOffer(s.items)?.item.id ?? null);
  const homeOfferReference = useAudioQueueStore(
    (s) => homeInlineSaveOffer(s.items)?.scriptureAnchor?.reference ?? null,
  );
  // Mic repair F2 (2026-10-20): the banner names the recording it means (its
  // capture time) so it reads apart from a prayer just saved, and says when no
  // words were heard in it (no Save is offered for that recording).
  const homeOfferCapturedAt = useAudioQueueStore(
    (s) => homeInlineSaveOffer(s.items)?.item.capturedAt ?? null,
  );
  const homeOfferFailedWithoutWords = useAudioQueueStore((s) => {
    const offer = homeInlineSaveOffer(s.items);
    return offer !== null && inlineSaveOfferFailedWithoutWords(offer.item);
  });
  const homeOfferHeardNoWords = useAudioQueueStore((s) => {
    const offer = homeInlineSaveOffer(s.items);
    return offer !== null && inlineSaveOfferHeardNoWords(offer.item);
  });
  // RU item 3 (2026-10-20): recordings the resume card's "Not now" set aside
  // in this page. Home lists them in an entry, and focus moves there when one
  // is set aside (the card's button that had it is gone), but only once no
  // card remains: with another recording behind it, the card keeps focus
  // (fix round 1, F3). Recordings whose words already reached the draft
  // (fix round 1, F2) are listed apart, never with "then save it"; the draft
  // records them (DS, 2026-10-20), so their entry asks no "discard it later".
  const deferredRecordingCount = voice.deferredInterruptions.length;
  const deferredWaitingCount = voice.deferredInterruptions.filter((entry) => !entry.wordsMoved).length;
  const deferredMovedCount = deferredRecordingCount - deferredWaitingCount;
  const resumeCardOpen = voice.interruptedSessions.length > 0;
  const deferredEntryRef = useRef<HTMLDivElement>(null);
  const deferredCountSeenRef = useRef(deferredRecordingCount);
  useEffect(() => {
    const previous = deferredCountSeenRef.current;
    deferredCountSeenRef.current = deferredRecordingCount;
    if (deferredRecordingCount <= previous || resumeCardOpen) return;
    const active = document.activeElement;
    if (active === null || active === document.body) deferredEntryRef.current?.focus();
  }, [deferredRecordingCount, resumeCardOpen]);
  const fallbackReason = useSettingsStore((s) => s.fallbackReason);
  const defaultPrivate = useSettingsStore((s) => s.defaultPrivate);
  const isInitialSyncing = useSyncStore((s) => s.isInitialSyncing);
  const isPrayerTarget = voice.target === 'prayer';
  const isListening = voice.isRecording && isPrayerTarget;
  const isWriting = voice.isTranscribing && isPrayerTarget;
  const interim = isPrayerTarget ? voice.interim : '';

  // Core-content cutover. `readOnly` hides the compose affordances (save,
  // hero mic, tap-to-write, keep-praying, clear) and shows the
  // maintenance banner. It is PRESENTATION: the guarantee that a write
  // cannot land is the shared guard at the store + db layers, which
  // rejects a bypassed call regardless of what this page renders.
  const readOnly = isCoreReadOnly();
  const enhancedVoiceEnabled = enhancedVoiceAllowed();

  useEffect(() => {
    return registerVoiceTarget('prayer', {
      onFinal: (finalText, queueItemId) => {
        if (queueItemId) {
          committedInlineAudioIdsRef.current.add(queueItemId);
          heldWordsRef.current.set(queueItemId, finalText);
          // Persist the exact identity before notifying React. If the page is
          // killed before the draft text effect runs, mount recovery can read
          // this row's already-durable transcript and rebuild the draft.
          persistPrayerDraftAudioIds(committedInlineAudioIdsRef.current);
        }
        setText((prev) => mergeRecoveredVoiceText(prev, finalText));
      },
      // Browser SpeechRecognition can have committed final words in the
      // draft plus a trailing interim fragment at the instant the tab hides.
      // Persist both; otherwise Keep typing could replace the durable draft
      // with only the trailing fragment.
      getRecoveryText: () => textRef.current,
    });
  }, [registerVoiceTarget]);

  const displayValue = interim
    ? text
      ? text + ' ' + interim
      : interim
    : text;

  useEffect(() => {
    loadPrayers();
  }, [loadPrayers]);

  // "Tap Save to keep this prayer" reassurance cue — briefly pulses the
  // Save button + shows a fading hint once the user finishes recording OR
  // after a long pause in typing. See useSaveCue for trigger rules.
  const saveCueWanted = useSaveCue(isListening || isWriting, text);
  // DS fix round 1 (REVIEW-DS1 F4): while the held-recording alert asks for a
  // decision, "Tap Save to keep this prayer" and the glowing Save would say the
  // opposite, so the cue stays off.
  const showSaveCue = saveCueWanted && heldConflict === null;

  // Placement for the Clear pill: pinned to the bottom-right resting slot,
  // just above the dock and the iOS keyboard edge, for ALL text lengths. It
  // used to follow the bottom edge of the user's text, but on a short prayer
  // the text end is near the top, so the pill snapped up into the first lines
  // and overlapped what the user was typing — the bottom-right pin keeps it
  // permanently clear of the typing zone. Hidden while actively listening /
  // transcribing so it doesn't compete with the mic-active visuals.
  //
  // The vertical slot is derived from visualViewport so the pill stays above
  // the keyboard when it's open and above the dock when it's closed.
  const showClear =
    !readOnly && !!text.trim() && !isSaving && !confirmingClear && !isListening && !isWriting;

  useEffect(() => {
    if (!showClear) return;
    const compute = () => {
      const ta = textareaRef.current;
      const container = containerRef.current;
      if (!ta || !container) return;

      const taOffsetTop = ta.offsetTop;

      const containerRect = container.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportBottomAbs = vv ? vv.height + vv.offsetTop : window.innerHeight;
      const viewportBottomInContainer = viewportBottomAbs - containerRect.top;
      // Pin the pill to its bottom-right resting slot, clear of the typing zone.
      // It deliberately does NOT follow the end of the typed text: a
      // text-following pill snapped up into the first lines on a short prayer
      // and overlapped what the user was actively typing.
      //
      // The reserve below the slot depends on whether the iOS keyboard is open,
      // because the 100dvh .container does NOT shrink under the keyboard:
      //  - keyboard OPEN: the dock sits below the keyboard top, off-screen, so
      //    viewportBottomInContainer already IS the keyboard top. Reserve only
      //    the pill + breath (52px). Subtracting the full ~336px dock here would
      //    push maxTop up by a dock height — negative on small phones — and
      //    Math.max would clamp the pill back into the first lines (the bug).
      //  - keyboard CLOSED: the dock occupies the visible viewport bottom, so
      //    the pill must clear it (dockHeight + 52).
      // 100px threshold filters browser toolbar show/hide diffs (rule #11).
      const dockHeight = dockRef.current?.offsetHeight ?? 0;
      const keyboardInset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      const reserve = keyboardInset > 100 ? 52 : dockHeight + 52;
      const maxTop = viewportBottomInContainer - reserve;

      const desired = Math.max(maxTop, taOffsetTop + 16);
      setClearTop(desired);
    };

    // Debounce so the pill doesn't recompute on every keystroke or STT word.
    const timer = setTimeout(compute, 220);
    return () => clearTimeout(timer);
  }, [text, interim, showClear]);

  useEffect(() => {
    const ta = textareaRef.current;
    const container = containerRef.current;
    if (!ta || !container) return;

    const recompute = () => {
      if (!text.trim()) return;
      const taOffsetTop = ta.offsetTop;
      const containerRect = container.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportBottomAbs = vv ? vv.height + vv.offsetTop : window.innerHeight;
      const viewportBottomInContainer = viewportBottomAbs - containerRect.top;
      const dockHeight = dockRef.current?.offsetHeight ?? 0;
      // Keyboard open: the dock is off-screen below the keyboard, reserve only
      // the pill/breath; closed: clear the dock. (See the compute effect above.)
      const keyboardInset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      const reserve = keyboardInset > 100 ? 52 : dockHeight + 52;
      const maxTop = viewportBottomInContainer - reserve;
      // Pin bottom-right (see the compute effect above) — never follow text.
      const desired = Math.max(maxTop, taOffsetTop + 16);
      setClearTop(desired);
    };

    const vv = window.visualViewport;
    ta.addEventListener('scroll', recompute);
    window.addEventListener('resize', recompute);
    vv?.addEventListener('resize', recompute);
    vv?.addEventListener('scroll', recompute);
    return () => {
      ta.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
      vv?.removeEventListener('resize', recompute);
      vv?.removeEventListener('scroll', recompute);
    };
  }, [text]);

  // Keyboard settle (smooth pass on v0.3.0). The composer's visible height
  // (containerHeight and keyboardOpen, from which the three-band layout is
  // measured) follows the visual viewport, and a soft keyboard moves that in
  // steps: several resize events while it animates on Android Chrome and in
  // the stepped stand-ins, usually one or two final values on iOS Safari, and
  // scroll events while Safari pans a field into view. Committing every step
  // moved the dock (mic, tap-to-write, garden pill) and the textarea in 40 to
  // 55 px jumps, up and then back down at the whole-page switch, and
  // re-cropped the scene each time. A layout-viewport change (rotation, a
  // window resize) still commits at once, and so does an unobstructed visual
  // viewport (inset under 2 px: the keyboard has closed). Any other visual
  // viewport change commits once the viewport has been still for
  // KEYBOARD_SETTLE_MS, and never during the welcome's leaving fade: what the
  // keyboard did while it faded is committed when the fade ends (once the
  // viewport has been still for KEYBOARD_SETTLE_MS, so a keyboard still moving
  // as the fade ends, a slow one or the 160 ms reduced-motion fade, still moves
  // the dock once), so nothing under the scrim moves while it fades. Only the
  // timing changes; the values and the composer's resting-height guards (inset
  // under 2 px) are as before.
  const lastViewportChangeRef = useRef(Number.NEGATIVE_INFINITY);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const vv = window.visualViewport;
    let layoutHeight = window.innerHeight;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const commit = () => {
      timer = undefined;
      const rect = container.getBoundingClientRect();
      const viewportBottomAbs = vv ? vv.height + vv.offsetTop : window.innerHeight;
      setContainerHeight(viewportBottomAbs - rect.top);
      setKeyboardOpen((vv ? Math.max(0, window.innerHeight - vv.height) : 0) > 100);
    };
    const onViewportChange = () => {
      lastViewportChangeRef.current = performance.now();
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      const layoutChanged = window.innerHeight !== layoutHeight;
      layoutHeight = window.innerHeight;
      if (layoutChanged) { commit(); return; }
      if (gateLeaving) return;
      const inset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      if (inset < 2) commit();
      else timer = setTimeout(commit, KEYBOARD_SETTLE_MS);
    };
    if (!gateLeaving) {
      // At mount (no change seen yet, so at once) and when the leaving fade ends.
      const inset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      const still = performance.now() - lastViewportChangeRef.current;
      if (inset < 2 || still >= KEYBOARD_SETTLE_MS) commit();
      else timer = setTimeout(commit, KEYBOARD_SETTLE_MS - still);
    }
    window.addEventListener('resize', onViewportChange);
    vv?.addEventListener('resize', onViewportChange);
    vv?.addEventListener('scroll', onViewportChange);
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      window.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('resize', onViewportChange);
      vv?.removeEventListener('scroll', onViewportChange);
    };
  }, [gateLeaving]);

  // App notice stack clearance (testimony plan rev 4 section 32.2). The stack is
  // fixed (viewport pixels); the feedback area is absolute in this container, so
  // both are converted to container pixels here before any space is compared.
  // The cap derives from the stack's uncapped height and fixed geometry only,
  // never from its capped height, so it cannot feed back on itself.
  const ordinaryFeedbackLimitPx =
    showClear && clearTop !== null ? clearTop - 8 : keyboardOpen ? containerHeight - 8 : null;
  const noticeStack = useSyncExternalStore(subscribeNoticeStackGeometry, getNoticeStackGeometry, () => null);
  const [undoRowHeight, setUndoRowHeight] = useState(0);
  useEffect(() => {
    if (!undoSlot) return;
    const measure = () => setUndoRowHeight(undoSlot.offsetHeight);
    const sizes = new ResizeObserver(measure);
    sizes.observe(undoSlot);
    measure();
    return () => sizes.disconnect();
  }, [undoSlot]);
  const [noticeClearance, setNoticeClearance] = useState(0);
  // While an overlay covers Home (any nav layer open), an open keyboard belongs
  // to that overlay (the Garden search, an update sheet), not to Home's compose
  // area. Following Home's keyboard bound there would resize the stack, and
  // every surface that reserves space below it, under the field being typed
  // into (v0.2.0 finishing repair: the Garden search row moved 30 to 36 px at
  // 375 px with Extra Large text). So while Home is covered and the keyboard is
  // open, the cap keeps Home's last keyboard-closed bound; the stack's own
  // height and Undo's row still apply. Uncovered Home is unchanged.
  const homeCovered = useSyncExternalStore(nav.subscribe, () => nav.length() > 0);
  const restingBottomRef = useRef<number | null>(null);
  const restingShortHeightRef = useRef(false);

  // Home unmount restores the stack's normal geometry.
  useEffect(() => () => setNoticeStackCap(null), []);

  // Bible index snapshot is single-use; Home is the hub, so any route through
  // Home wipes a stale browse/search snapshot before a fresh Bible visit
  // (README §6.1, navigation chunk C). Cheap idempotent clear.
  useEffect(() => {
    clearBibleIndexSnapshot();
  }, []);

  // Historical progress notes are authored content. Opening Home must not
  // delete matching words or re-date a prayer, even for legacy hint-shaped text.

  // Prayer enrichment (parse + gardenSummary) was lifted out of PrayPage
  // into the app-wide usePrayerEnrichment hook (mounted once in App) so
  // prayers created from any surface (home compose and the Bible
  // Pray-mode sheet) get enriched regardless of route. It remains the
  // SOLE enrichment writer, preserving the single-writer invariant from
  // commit 08693fb.

  useEffect(() => {
    let mounted = true;
    const draft = loadPrayerDraft();
    const persistedIds = loadPrayerDraftAudioIds();
    const initiallyPersisted = new Set(persistedIds);
    committedInlineAudioIdsRef.current = new Set(persistedIds);
    if (draft) setText(draft);

    void Promise.all(
      persistedIds.map((queueItemId) =>
        getInlineAudioDraftRecovery(queueItemId, 'prayer'),
      ),
    ).then(async (recoveries) => {
      if (!mounted) return;
      // Keep any capture that arrived after this mount probe started, while
      // pruning persisted IDs the recovery no longer returns (another owner or
      // source). DS fix round 1: a missing row is returned and kept.
      const liveIds = new Set(
        recoveries.flatMap((recovery) => recovery ? [recovery.queueItemId] : []),
      );
      for (const queueItemId of committedInlineAudioIdsRef.current) {
        if (!initiallyPersisted.has(queueItemId)) liveIds.add(queueItemId);
      }
      committedInlineAudioIdsRef.current = liveIds;
      persistPrayerDraftAudioIds(liveIds);

      let restored = textRef.current;
      for (const recovery of recoveries) {
        if (recovery?.transcribedText) {
          restored = mergeRecoveredVoiceText(restored, recovery.transcribedText);
        }
      }
      // DS fix round 1 (REVIEW-DS1 F1, F2), memory only: the words the Save's
      // held-recording check compares with. A recording that is still here
      // gives its own words (also when it is linked or consumed elsewhere, so
      // a row deleted later still counts). A recording whose row is already
      // gone has no words left to read, so the draft restored at this mount
      // stands in for them: while the text still carries that draft, the
      // Save says it cannot tell whether the recording was saved (never
      // "saved"). Released on Clear, "Save as a separate prayer" and any
      // successful Save. The stored draft-ids value stays ids only.
      const restoredDraft = restored.trim();
      for (const recovery of recoveries) {
        if (!recovery) continue;
        const words = recovery.rowMissing
          ? restoredDraft
          : recovery.heldWords ?? recovery.transcribedText ?? '';
        if (words) heldWordsRef.current.set(recovery.queueItemId, words);
      }
      if (restored !== textRef.current) setText(restored);
      const owner = getAuthUserId();
      if (owner && (await listPrayerAttempts(owner)).length > 0 && mounted) setGateBypassed(true);
      if (mounted) setDraftReady(true);
    }).catch(() => { if (mounted) { setGateBypassed(true); setDraftReady(true); } });

    return () => {
      mounted = false;
    };
    // The durable draft and companion IDs are read exactly once per mount.
  }, []);

  useEffect(() => {
    // Core-content cutover: adia-prayer-draft is in the preservation set,
    // and this effect is a WRITE path independent of Save. Both branches
    // are destructive to a pre-cutover draft: the setItem branch
    // overwrites it, and the removeItem branch deletes it outright the
    // moment the field reads empty. Freeze the key entirely while
    // read-only, so what the escrow captures on the device is what was
    // there before the gate opened.
    if (readOnly) return;
    persistPrayerDraft(text);
  }, [text, readOnly]);

  // App-update safety: right before an update reload, write the composer text
  // and its committed inline-audio ids once more. The effects above already
  // persist both on every change; this only closes the last-keystroke window.
  // Declared AFTER the mount-restore effect so both refs are hydrated from
  // storage before a flush can ever run. Frozen while read-only, like the
  // draft effect.
  useEffect(() => {
    if (readOnly) return;
    return registerDraftFlusher(() => {
      persistPrayerDraft(textRef.current);
      persistPrayerDraftAudioIds(committedInlineAudioIdsRef.current);
    });
  }, [readOnly]);

  useEffect(() => {
    // No autofocus while read-only: the field cannot be typed into, so
    // focusing it only raises the iOS keyboard over the content the user
    // came to read.
    if (readOnly || !composeMode || topmostModal()) return;
    // A welcome choice commits inside chooseGateMode's flushSync and React runs
    // this effect synchronously at the end of that commit, before
    // chooseGateMode's own focus call: this is the call that focuses the field
    // inside the tap, so it must not scroll either. A field that already has
    // focus is left alone (every focus() call forces a style update).
    const field = textareaRef.current;
    if (!field || document.activeElement === field) return;
    field.focus({ preventScroll: true });
  }, [readOnly, composeMode]);

  // Core-content cutover: the Save button is hidden while read-only, so
  // this wrapper only fires if something bypasses the render gate (a
  // stale frame, an injected click, a keyboard shortcut a future chunk
  // adds). Swallow the guard rejection instead of letting an unhandled
  // error tear down the page; the user's typed text stays in the
  // textarea and in the localStorage draft either way.
  const saveLatchRef = useRef(false);
  const createSessionRef = useRef<PrayerAuthoringSession | null>(null);
  const createCandidateRef = useRef<Prayer | null>(null);
  const handleSave = async () => {
    if (saveLatchRef.current) return;
    saveLatchRef.current = true;
    // A new Save is the person acting: an earlier reopen alert leaves (a
    // repeated refusal shows it again, and is announced again).
    setSaveReopen(null);
    setHeldConflict(null);
    // The same for the previous Save's outcome line: a standing "Couldn't save
    // this prayer" must not stay on screen while this Save is still being
    // checked. This Save's own outcome, if it has one, replaces it (DS5).
    setStatus(null);
    // Covers the whole latch, including the part before isSaving flips.
    const releaseUpdateHold = holdUpdate('prayer-save-latch');
    try {
      await doSave();
    } catch (err) {
      setIsSaving(false);
      if (err instanceof CoreReadOnlyError) return;
      setSaveReopen(null);
      setStatus('failed');
    } finally {
      saveLatchRef.current = false;
      releaseUpdateHold();
    }
  };

  const doSave = async () => {
    if (!createSessionRef.current) createSessionRef.current = new PrayerAuthoringSession(prayerEditorAuth());
    const interimAtStart = interim;
    const wasActive = isListening || isWriting;
    if (isListening) await voice.stop();
    // Enhanced mode may have just appended transcribed text via onFinal;
    // read the fresh value from the ref rather than the stale closure.
    const latestText = textRef.current;
    // DS fix round 1 (REVIEW-DS1 F2): the recordings this composer held when
    // this Save took its text; a successful Save releases all of them (the
    // composer is then empty). A recording that arrives later stays held.
    const heldAtStart = [...committedInlineAudioIdsRef.current];
    const merged = interimAtStart
      ? latestText
        ? latestText + ' ' + interimAtStart
        : interimAtStart
      : latestText;
    const trimmed = merged.trim();

    // V.1.1 inline-save rescue. The user awaited a recording that
    // ended without producing text — the most common cause is
    // transcribeAudio failing while the user is in a network dead
    // zone. The audio is already on disk: useSpeechToText's
    // onstop-catch persisted it to IDB via enqueueAudio with
    // awaitingInlineSave: true the moment the transcribe call
    // rejected. Save claims the orphan, creates a fresh Prayer with
    // pendingTranscriptionId set to the queue item id. One IDB transaction
    // creates the Prayer and binds the audio destination.
    if ((wasActive || voice.inlineSaveSavable) && !trimmed) {
      // RU item 2 (2026-10-20): right after a recording made here, Save claims
      // that recording (it has no passage). Otherwise Home's offer is why Save
      // is shown, and Save claims exactly the recording the banner names
      // (homeInlineSaveOffer), with its own validated Bible passage when it
      // has one; the link transaction checks that passage again. Mic repair F2:
      // never a recording in which no words were heard (offered for Discard
      // only; claimPendingInlineSave refuses it the same way).
      const offered = wasActive ? null : homeInlineSaveOffer(useAudioQueueStore.getState().items);
      const offer = offered && !inlineSaveOfferFailedWithoutWords(offered.item) ? offered : null;
      const claim = offer
        ? { queueItemId: offer.item.id, scriptureAnchor: offer.scriptureAnchor }
        : wasActive
          ? await voice.claimPendingInlineSave().then((claimed) =>
              claimed ? { queueItemId: claimed.queueItemId, scriptureAnchor: null } : null)
          : null;
      if (claim) {
        const offeredMode = claim.scriptureAnchor ? 'prayer' : wasActive ? composeMode : recoveryModes[claim.queueItemId];
        if (!offeredMode) {
          setRequiredRecoveryId(claim.queueItemId); recoveryChoiceRef.current?.focus(); return;
        }
        const prayerId = crypto.randomUUID();
        const now = new Date().toISOString();
        const pendingPrayer: Prayer = {
          id: prayerId,
          rawText: '',
          ...entrySeed(offeredMode, now),
          progressHistory: [],
          createdAt: now,
          updatedAt: now,
          isPrivate: defaultPrivate || undefined,
          pendingTranscriptionId: claim.queueItemId,
          ...(claim.scriptureAnchor ? { scriptureAnchor: claim.scriptureAnchor } : {}),
        };
        // A claim is a save: the welcome (gateSafe) and every other saving
        // gate stay closed between the offer leaving and the saved
        // confirmation, exactly as during a typed Save.
        setIsSaving(true);
        try {
          await createPendingPrayerFromAudio(pendingPrayer, claim.queueItemId);
          // DS: written only if this page held it, so a page that never held
          // the recording does not erase another page's draft association.
          releaseHeldRecordings([claim.queueItemId, ...heldAtStart]);
          // Confirmation visual reuses the existing 'saved' state —
          // same affordance the user already knows. The pending pill
          // at the top of PrayPage will show "Finishing 1 prayer when
          // online." and the new vine card carries the pending icon
          // until the queue processor fills rawText.
          try { localStorage.setItem(VISITED_KEY, '1'); } catch { /* harmless */ }
          const nextStatus: SaveStatus = getOnline() ? 'saved' : 'savedOffline';
          setStatus(nextStatus);
          setTimeout(() => setStatus(null), nextStatus === 'savedOffline' ? SAVED_OFFLINE_CONFIRM_MS : SAVED_CONFIRM_MS);
        } catch (err) {
          console.error('[Adia] V.1.1 inline-save claim failed', err);
          // A stale tab's duplicate Save: createPendingPrayerFromAudio reports
          // `pending-prayer-audio-already-saved` only after proving from durable
          // state that this same recording already has its valid destination
          // (another page's Prayer that still names it as pending, or that it
          // is bound to), so the user's Save is done and the offer has left.
          // Any other refusal, including an already-linked row whose Prayer is
          // missing, is an honest failure; the recording and the row stay
          // exactly where they were. The saved outcome follows the same
          // online/offline wording as every other save.
          if (err instanceof Error && err.message === PENDING_PRAYER_ALREADY_SAVED) {
            releaseHeldRecordings(heldAtStart);
            const nextStatus: SaveStatus = getOnline() ? 'saved' : 'savedOffline';
            setStatus(nextStatus);
            setTimeout(() => setStatus(null), nextStatus === 'savedOffline' ? SAVED_OFFLINE_CONFIRM_MS : SAVED_CONFIRM_MS);
          } else if (saveRefusedForUnreadableRecording(err)) {
            // LC10 item 3: the persistent reopen alert, not the timed status.
            setStatus(null);
            setSaveReopen('recording');
          } else {
            setSaveReopen(null);
            setStatus('failed');
            setTimeout(() => setStatus(null), SAVED_CONFIRM_MS);
          }
        } finally {
          setIsSaving(false);
        }
        return;
      }
    }

    if (!trimmed) return;

    setIsSaving(true);
    // DS (duplicate-save follow-through, 2026-10-20): recordings whose words
    // this composer holds but that were saved, or may have been saved, as a
    // prayer elsewhere (another page's Save, the resume card's Save what I
    // have). Saving now would silently turn one recording into two prayers, so
    // nothing is written: the words stay and the alert explains, with an
    // explicit "Save as a separate prayer". Decided by the recordings' identity
    // and stored state; a read that fails saves nothing (fail closed).
    let heldConflicts: Awaited<ReturnType<typeof findHeldRecordingConflicts>>;
    try {
      heldConflicts = await findHeldRecordingConflicts(
        new Map([...committedInlineAudioIdsRef.current].map((queueItemId) =>
          [queueItemId, heldWordsRef.current.get(queueItemId) ?? ''] as const)),
        trimmed,
        null,
      );
    } catch {
      setIsSaving(false);
      setStatus('failed');
      return;
    }
    if (heldConflicts.saved.length + heldConflicts.unknown.length > 0) {
      setIsSaving(false);
      setStatus(null);
      setHeldConflict({
        kind: heldConflicts.unknown.length === 0 ? 'saved' : 'unknown',
        ids: [...heldConflicts.saved, ...heldConflicts.unknown],
      });
      return;
    }
    // A persisted home draft and its one unresolved create attempt resume the
    // same identity after reload. Ambiguous attempts require explicit review.
    if (!createCandidateRef.current && !createSessionRef.current.attempt) {
      const candidates = (await listPrayerAttempts(createSessionRef.current.auth.ownerId)).filter(attempt =>
        attempt.intent.kind === 'create' && !attempt.intent.prayer.scriptureAnchor && attempt.intent.prayer.rawText === trimmed);
      if (candidates.length > 1) { setIsSaving(false); return; }
      if (candidates[0]?.intent.kind === 'create') {
        createSessionRef.current.attempt = candidates[0];
        createCandidateRef.current = candidates[0].intent.prayer;
      }
    }
    const frozenMode = createCandidateRef.current ? (isPraise(createCandidateRef.current) ? 'praise' : 'prayer') : null;
    if (frozenMode) {
      setCapturedMode(frozenMode);
      if (composeMode !== frozenMode) {
        setComposeMode(frozenMode); setModeRequired(false); setIsSaving(false);
        modeChoiceRef.current?.focus();
        return;
      }
    }
    if (!composeMode) { setIsSaving(false); requestMode(); return; }
    const saveAuth = createSessionRef.current.auth;
    const id = createCandidateRef.current?.id ?? crypto.randomUUID();
    const now = createCandidateRef.current?.createdAt ?? new Date().toISOString();

    const prayer: Prayer = {
      id,
      ...entrySeed(composeMode, now),
      progressHistory: [],
      createdAt: now,
      updatedAt: now,
      // Apply the user's default privacy posture at save time. The
      // per-prayer override now lives solely in PrayerDetail's bottom
      // actions — compose stays focused on the prayer itself.
      isPrivate: defaultPrivate || undefined,
      // Preserve the captured create's type/anchors while revising only its text.
      ...(createCandidateRef.current ?? {}),
      rawText: trimmed,
    };

    createCandidateRef.current = prayer;
    setCapturedMode(isPraise(prayer) ? 'praise' : 'prayer');
    const inlineAudioClaims = await collectInlineAudioCommitClaims(
      committedInlineAudioIdsRef.current,
      'prayer',
      trimmed,
    ).catch(() => []);
    try {
      const result = await createSessionRef.current.save(prayer.id, { kind: 'create', prayer }, inlineAudioClaims);
      if (result.kind !== 'committed') throw new Error('prayer-attempt-not-pending');
    } catch (error) {
      setIsSaving(false);
      if (prayerEditorIsCurrent(saveAuth)) {
        if (saveRefusedForUnreadableRecording(error)) {
          // LC10 item 3: the persistent reopen alert, not the timed status.
          setStatus(null);
          setSaveReopen('words');
        } else {
          setSaveReopen(null);
          setStatus('failed');
        }
      }
      // The failure copy stands (nothing was saved). A claimed recording that
      // another page's discard took meanwhile leaves this page's offer.
      if (inlineAudioClaims.length > 0) void refreshInlineAudioMirror(inlineAudioClaims.map((claim) => claim.queueItemId));
      return;
    }
    if (!prayerEditorIsCurrent(saveAuth)) return;
    createSessionRef.current = null;
    createCandidateRef.current = null;
    setCapturedMode(null);
    setText('');
    setIsSaving(false);
    // The commit bound the consumed recordings; this page's mirror learns it
    // now, so its own inline-save offer (and its Discard) leaves at once.
    if (inlineAudioClaims.length > 0) void publishCommittedInlineAudio(inlineAudioClaims.map((claim) => claim.queueItemId));
    if (inlineAudioClaims.length > 0) void syncContentLifecycle().then(async () => {
      for (const claim of inlineAudioClaims) await finalizeCommittedAudio(claim.queueItemId).catch(() => false);
    }).catch(() => {});
    const claimedInlineIds = new Set(
      inlineAudioClaims.map((claim) => claim.queueItemId),
    );
    // Remove the sources consumed by this transaction and (DS fix round 1)
    // every recording held when this Save took its text: the composer is now
    // empty. A source that arrived during the Save is retained;
    // asynchronous scans must not replace a newer set.
    releaseHeldRecordings([...claimedInlineIds, ...heldAtStart]);

    // Mark this device as "returning user" for the inline routing script.
    // Wrapped because private-mode Safari throws on localStorage writes and
    // we don't want to fail a successful save over a nice-to-have flag.
    try {
      localStorage.setItem(VISITED_KEY, '1');
    } catch {
      /* storage disabled — harmless */
    }

    const nextStatus: SaveStatus = getOnline() ? 'saved' : 'savedOffline';
    setStatus(nextStatus);
    setTimeout(() => setStatus(null), nextStatus === 'savedOffline' ? SAVED_OFFLINE_CONFIRM_MS : SAVED_CONFIRM_MS);

    const moment = checkDiscoveryMoment(prayers.filter(p => !isPraise(p)));
    if (moment) {
      setTimeout(() => {
        setDiscovery(moment);
        markDiscoveryShown();
        setTimeout(() => setDiscovery(null), 8000);
      }, 2500);
    }

    // Enrichment (parse + gardenSummary derivation) is handled exclusively
    // by the unparsed-prayer effect above, which re-reads latest store state
    // and writes a complete object. handleSave intentionally no longer runs
    // its own parse: a second concurrent parser was the source of the
    // gardenSummary-loss race (a stale parsed-only write clobbering the
    // summary). See docs/plans/garden-summary-loss-fix-2026-05-29.md.
  };

  const toggleMic = () => {
    if (isListening) {
      if (interim) {
        setText((prev) => (prev ? prev + ' ' + interim : interim));
      }
      void voice.stop().catch(() => {});
      return;
    }
    voice.start('prayer');
  };

  // The rescue banner's Discard (stream C, 2026-10-19). Its outcome comes from
  // the offer selection, not from the promise alone: the recording it is for
  // is the one the discard itself takes (homeInlineSaveOffer, read before
  // the call), and if that recording is still offered once the discard
  // settled, the discard did not happen ('kept'): it rejected, or ended with no
  // registry answer and was withdrawn. The data layer keeps the recording
  // either way. A recording that left the offer (removed, or no longer this
  // page's to discard) is 'settled', and the banner behaves as before.
  const handleRescueDiscard = async (): Promise<RescueDiscardOutcome> => {
    const offeredId =
      homeInlineSaveOffer(useAudioQueueStore.getState().items)?.item.id ?? null;
    let removedIds: string[] = [];
    try {
      removedIds = await voice.clearPendingInlineSave('prayer');
      releaseHeldRecordings(removedIds);
    } catch (error) {
      console.error(
        '[Adia] rescue-discard-failed',
        error instanceof Error ? error.name : typeof error,
      );
    }
    // A Discard that settled took the recording a reopen alert was about, so
    // the alert leaves with it (LC10 item 3); a kept recording keeps it.
    if (offeredId === null || removedIds.includes(offeredId)) {
      setSaveReopen(null);
      return 'settled';
    }
    const stillOffered = useAudioQueueStore.getState().items.some(
      (item) => item.id === offeredId && homeInlineSaveOffer([item]) !== null,
    );
    if (!stillOffered) setSaveReopen(null);
    return stillOffered ? 'kept' : 'settled';
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isListening || interim) {
      void voice.stop().catch(() => {});
    }
    setText(e.target.value);
    if (status) setStatus(null);
    if (saveReopen) setSaveReopen(null);
    if (heldConflict) setHeldConflict(null);
  };

  // DS (2026-10-20): the explicit, intentional choice after the held-recording
  // alert: these recordings are no longer this composer's, so the words are
  // saved as a separate prayer (claiming nothing for them; any other recording
  // the composer still holds is claimed as usual).
  const handleSaveSeparate = () => {
    const ids = heldConflict?.ids ?? [];
    releaseHeldRecordings(ids);
    setHeldConflict(null);
    void handleSave();
  };
  // DS fix round 1 (REVIEW-DS1 F4): close the alert and go back to the words;
  // nothing is saved or released, and the next Save checks again.
  const handleKeepEditing = () => {
    setHeldConflict(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };
  useEffect(() => {
    // The Save that raised the alert left nothing to act on in the dock; focus
    // goes to the explanation, and Tab then reaches its actions.
    if (heldConflict) heldConflictRef.current?.focus();
  }, [heldConflict]);

  const openDetail = (id: string) => {
    if (isListening) void voice.stop().catch(() => {});
    setDetailInitialTestimony(null);
    setDetailId(id);
  };

  const handleDetailClose = () => {
    setDetailInitialTestimony(null);
    setDetailId(null);
  };

  // Settings "Notes you started" Open (testimony plan rev 4 section 9.2; Step 3
  // proposal section 3.2). Presentation only: it opens a prayer and asks that
  // prayer's detail to open its testimony dialog, and writes nothing. The
  // request is claimed for this history entry and this effect run, validated
  // after every asynchronous read, and consumed exactly once. A StrictMode
  // re-run takes it over; an unmount, any navigation, an account or session
  // change, a missing prayer or draft, an open layer, read-only mode, or an
  // interrupted recording that needs attention drops it without a trace.
  const location = useLocation();
  const locationKeyRef = useRef(location.key);
  locationKeyRef.current = location.key;
  const prayersReady = useAdiaStore((state) => state.hydration.state === 'ready');
  useLayoutEffect(() => {
    const request = initialPresentationRef.current;
    if (!request || !prayersReady) return;
    initialPresentationRef.current = undefined;
    // Presentation only, under the ordinary authorized Home runtime. Reuse
    // its existing detail, editing and deletion flows; never replay a request
    // after an account/session change or create an owner-scoped mutation here.
    if (isCoreReadOnly() || getAuthUserId() !== request.ownerId ||
        getAuthEpoch() !== request.authEpoch || nav.length() !== 0) return;
    if (request.prayerId && prayers.some((prayer) => prayer.id === request.prayerId)) {
      setDetailId(request.prayerId);
    } else if (request.focusWrite) {
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [prayersReady, prayers, nav]);
  const interruptedRef = useRef(voice.interruptedSessions);
  interruptedRef.current = voice.interruptedSessions;
  const openRunRef = useRef(0);
  useEffect(() => {
    const routeKey = location.key;
    const runToken = ++openRunRef.current;
    const request = claimTestimonyOpenRequest(routeKey, runToken);
    if (!request) return;
    let cancelled = false;
    const stillHere = () => !cancelled && locationKeyRef.current === routeKey &&
      testimonyOpenRequestPending(request.id);
    const sessionCurrent = () => getAuthUserId() === request.ownerId && getAuthEpoch() === request.authEpoch;
    if (prayersReady) {
      void (async () => {
        try {
          if (isCoreReadOnly() || !sessionCurrent()) {
            consumeTestimonyOpenRequest(request.id);
            return;
          }
          const readTarget = () => Promise.all([
            getContentOwnerBinding(),
            readTestimonyDraftSlot(request.ownerId, request.prayerId),
          ]);
          type Slot = Awaited<ReturnType<typeof readTestimonyDraftSlot>>;
          const targetValid = (binding: string | null, slot: Slot) => binding === request.ownerId &&
            !slot.foreign &&
            slot.draft?.ownerId === request.ownerId &&
            slot.draft.prayerId === request.prayerId &&
            slot.draft.draftId === request.draftId;
          const [binding, slot] = await readTarget();
          // An effect run that ended keeps its hands off: a StrictMode re-run
          // owns the request now, or the page is gone and the run's cleanup
          // ends it.
          if (!stillHere()) return;
          if (!sessionCurrent() || !targetValid(binding, slot)) {
            consumeTestimonyOpenRequest(request.id);
            return;
          }
          // Read the target again immediately before opening; it must be
          // byte-identical to the first read, so a change that landed while
          // the first read was in flight is never opened.
          const [bindingAgain, slotAgain] = await readTarget();
          if (!stillHere()) return;
          const prayer = useAdiaStore.getState().prayers.find((item) => item.id === request.prayerId);
          const valid = !isCoreReadOnly() &&
            sessionCurrent() &&
            targetValid(bindingAgain, slotAgain) &&
            slotAgain.raw === slot.raw &&
            prayer !== undefined &&
            nav.length() === 0 &&
            interruptedRef.current.length === 0;
          if (!consumeTestimonyOpenRequest(request.id) || !valid) return;
          openDetail(request.prayerId);
          setDetailInitialTestimony({ id: request.id, prayerId: request.prayerId });
        } catch {
          if (stillHere()) consumeTestimonyOpenRequest(request.id);
        }
      })();
    }
    return () => {
      cancelled = true;
      setTimeout(() => abandonTestimonyOpenRequest(request.id, runToken), 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- claim per history entry and readiness only
  }, [location.key, prayersReady]);

  const handleDetailDelete = async (id: string) => {
    // Snapshot BEFORE the delete mutates the store — we need the full
    // Prayer object to restore if the user taps Undo. S0 (PC-B rev 9,
    // section 10b): deletion goes through the owner-checked validity RPC
    // (durable intent journal + policy soft delete); the legacy
    // deletePrayerRemote path is retired. A refused delete shows the plain
    // `pray.deleteFailed` failure; a successful one shows only Undo (no
    // generic protection-pending toast) and claims no cross-device
    // completion.
    const snapshot = prayers.find((p) => p.id === id) ?? null;
    // P1: remember the deleting dialogs (detail + leaf) before they close, so
    // the notices pick their host only once these have unmounted.
    const noticeSource = captureNoticeSource(zDetail);
    // A2: close the `detail` layer (and the `leaf` above it) EXPLICITLY and
    // BEFORE the store delete — the delete mutates `prayers` before it
    // resolves, which nulls detailPrayer and unmounts PrayerDetail
    // mid-await; a post-await requestClose would arrive after the leaf
    // cleanup already won the popInFlight race and stranded a phantom
    // `detail` history entry.
    if (nav.has('detail')) nav.requestClose('detail');
    if (!snapshot) return;
    const result = await deletePrayerProtected(snapshot);
    setDetailId(null);
    if (!result.ok) {
      // Any refusal (the protected layer reports try-again or not-signed-in)
      // is an actionable failure with plain deletion copy, not keepsake copy.
      showProtectionNotice(t('pray.deleteFailed'), noticeSource, `delete:${snapshot.id}`);
      return;
    }
    clearFailureFor(`delete:${snapshot.id}`);
    // A successful delete shows only Undo. `result.pending` is always true
    // (background sync was started, not yet confirmed); it is not offline
    // state. Keepsake creation stays held until the change is confirmed and
    // says so itself (PC-B section 10b.2 amendment, 2026-10-15).
    if (snapshot) offerUndo(snapshot, noticeSource);
  };

  // Schedule / cancel the 7s auto-dismiss independently of handleDelete so
  // pointer/keyboard interaction on the toast can pause it. Elderly or
  // slow-reading users can hover to hold the toast open until they decide
  // (WCAG 2.2.1 "Enough Time" — adjustable via pause-on-interaction).
  const scheduleUndoDismiss = () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRecentlyDeleted(null);
      undoTimerRef.current = null;
    }, 7000);
  };

  const cancelUndoDismiss = () => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  };

  // Offer (or re-offer) Undo for this exact snapshot object; restore looks its
  // ticket up by identity. Latest-wins: a newer snapshot simply replaces it.
  // The timer starts now only if no pause reason applies; otherwise it starts
  // with a full 7 s when DeletionNotices reports every reason cleared.
  const offerUndo = (snapshot: Prayer, source: NoticeSource | null) => {
    noticeEventRef.current += 1;
    setUndoMeta({ event: noticeEventRef.current, source });
    setRecentlyDeleted(snapshot);
    if (!undoPausedRef.current) scheduleUndoDismiss();
  };

  // `failure` names the operation a failure belongs to (`delete:<id>`,
  // `restore:<id>`, `keepsake-job`) so only that operation's success clears it.
  const showProtectionNotice = (
    message: string,
    source: NoticeSource | null = null,
    failure: string | null = null,
  ) => {
    noticeEventRef.current += 1;
    const notice = { event: noticeEventRef.current, message, source };
    if (failure === null) setInfoNotice(notice);
    else setFailureNotice({ ...notice, origin: failure });
  };
  const clearFailureFor = (origin: string) => {
    setFailureNotice((current) => (current?.origin === origin ? null : current));
  };

  // Hover, focus, or a covering surface pause Undo; the full 7 s restarts only
  // once all of them clear (WCAG 2.2.1 pause-on-interaction, plus coverage).
  const handleUndoPausedChange = (paused: boolean) => {
    undoPausedRef.current = paused;
    if (paused) cancelUndoDismiss();
    else if (recentlyDeleted) scheduleUndoDismiss();
  };

  const handleUndoDelete = async () => {
    if (!recentlyDeleted) return;
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    const snapshot = recentlyDeleted;
    setRecentlyDeleted(null);
    // S0: undo is the `restore` mutation through the same RPC, from the
    // exact expected policy revision (the delete receipt refreshed the
    // cached pin); it preserves visibility (private/deleted restores to
    // private/live). The local row is re-added in pulled-mode — no push.
    const result = await restorePrayerProtected(snapshot);
    if (!result.ok) {
      showProtectionNotice(t('pray.restoreFailed'), null, `restore:${snapshot.id}`);
      // Re-offer the undo so the prayer is not silently stuck deleted.
      offerUndo(snapshot, null);
      return;
    }
    clearFailureFor(`restore:${snapshot.id}`);
    // A successful restore shows nothing new (same rule as delete above).
  };

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // Informational protection-message auto-dismiss (reading-time window; tap
  // dismisses). Keyed on the message text exactly as before, so re-showing
  // the same text does not restart it. Failure messages never expire.
  const infoMessage = infoNotice?.message ?? null;
  useEffect(() => {
    if (!infoMessage) return;
    const timer = setTimeout(() => setInfoNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [infoMessage]);

  // S0 keepsake job locator consumer (PC-B rev 9, 10a.2). The email link
  // landed as ?keepsakeJob=<uuid>; main.tsx already stripped it from
  // history into tab-scoped sessionStorage. Once central auth is READY,
  // DELETE the stored locator, then make the single expected-owner
  // status request; if ready, fetch the authenticated delivery. A
  // signed-out tab retains the locator until sign-in; a wrong account
  // receives the unified 404 (surfaced as the accountChanged copy).
  useEffect(() => {
    let cancelled = false;
    const tryConsume = async () => {
      if (cancelled) return;
      if (peekJobLocator() === null) return;
      if (!isAuthResolved()) {
        setTimeout(tryConsume, 500);
        return;
      }
      const ownerId = getAuthUserId();
      if (!ownerId) return; // signed out: retain within the tab until sign-in
      const preview = await beginKeepsakePreviewBindingForLocator();
      if (cancelled) return;
      if (preview === null) {
        setTimeout(tryConsume, 2_000);
        return;
      }
      const jobId = peekJobLocator();
      if (jobId === null) return;
      const status = await pollKeepsakeJobStatus(preview, jobId);
      if (cancelled) return;
      if (!status.ok) {
        // A unified 404 can be the wrong signed-in account. Retain this
        // non-capability locator so switching accounts in the same tab works.
        if (status.code !== 'try-again' && status.code !== 'not-found') {
          consumeJobLocator();
        }
        showProtectionNotice(
          t(status.code === 'not-found' ? 'keepsake.accountChanged' : 'keepsake.tryAgain'),
          null,
          'keepsake-job',
        );
        if (status.code === 'try-again') setTimeout(tryConsume, 5_000);
        return;
      }
      if (status.status === 'ready') {
        const delivery = await fetchKeepsakeDelivery(preview, jobId);
        if (cancelled) return;
        if (delivery.ok) {
          consumeJobLocator();
          clearFailureFor('keepsake-job');
          deliverKeepsake(delivery.blob, '');
        } else {
          if (delivery.code !== 'try-again') consumeJobLocator();
          showProtectionNotice(t('keepsake.invalidated'), null, 'keepsake-job');
        }
      } else if (
        status.status === 'invalidated' ||
        status.status === 'expired' ||
        status.status === 'revoking'
      ) {
        consumeJobLocator();
        showProtectionNotice(t('keepsake.invalidated'), null, 'keepsake-job');
      } else if (status.status === 'failed') {
        consumeJobLocator();
        showProtectionNotice(t('keepsake.tryAgain'), null, 'keepsake-job');
      } else {
        showProtectionNotice(t('people.keepsakeExportPreparing'));
        setTimeout(tryConsume, 5_000);
      }
    };
    void tryConsume();
    return () => {
      cancelled = true;
    };
    // Mount consumer: the locator is tab-scoped, not reactive state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openGarden = () => {
    setGardenOpen(true);
  };

  const closeGarden = () => {
    setGardenOpen(false);
  };

  // A3 (rule #78): uniform additive person open. Replaces the compound
  // handlePersonTap (which closed detail+remember and opened garden+person at
  // once). Every entry (Garden vine/grid, Detail who-name, Evidence person-name)
  // calls this -> a single PUSH of `person` above whatever opener is on top;
  // back returns to the opener (round-11 Q12). No compound, no bespoke swap.
  const openPerson = (who: string) => setPersonFor(who);
  const closePerson = () => setPersonFor(null);

  // person-detail lifecycle (round-12 B1): its OWN handlers, NOT the top-level
  // handleDetailClose/handleDetailDelete (which always clear top-level detailId).
  const personDetailClose = () => setPersonDetailId(null);

  const personDetailDelete = async (id: string) => {
    const snapshot = prayers.find((p) => p.id === id) ?? null;
    // P1: the deleting dialogs (person-detail + anything above it).
    const noticeSource = captureNoticeSource(zPersonDetail);
    // Pop person-detail (+ any leaf above it) EXPLICITLY and BEFORE the store
    // delete — same race as handleDetailDelete (A2 §4.1a): removePrayer mutates
    // `prayers` before resolving, unmounting this PrayerDetail mid-await, so a
    // post-await requestClose would arrive after a child cleanup won the
    // popInFlight race. The §3.4b guard is the separate backstop for EXTERNAL
    // (sync/store) deletions that never call this handler.
    if (nav.has('person-detail')) nav.requestClose('person-detail');
    if (!snapshot) return;
    const result = await deletePrayerProtected(snapshot);
    setPersonDetailId(null);
    if (!result.ok) {
      // Any refusal (the protected layer reports try-again or not-signed-in)
      // is an actionable failure with plain deletion copy, not keepsake copy.
      showProtectionNotice(t('pray.deleteFailed'), noticeSource, `delete:${snapshot.id}`);
      return;
    }
    clearFailureFor(`delete:${snapshot.id}`);
    // A successful delete shows only Undo. `result.pending` is always true
    // (background sync was started, not yet confirmed); it is not offline
    // state. Keepsake creation stays held until the change is confirmed and
    // says so itself (PC-B section 10b.2 amendment, 2026-10-15).
    if (snapshot) offerUndo(snapshot, noticeSource);
  };

  const handleNew = (requestedMode?: ComposeMode) => {
    if (requestedMode && !textRef.current.trim() && !voice.inlineSaveAvailable && !resumeCardOpen && !deferredRecordingCount && !createCandidateRef.current) chooseMode(requestedMode);
    // resetToBase() unwinds every open coordinator layer in one tick (garden /
    // remember / evidence / detail / keepsake / share-* / garden-update /
    // person / person-detail / person-update), firing each layer's close().
    // discovery is a transient (not a layer), so it still needs a direct clear.
    // (handleNew previously didn't clear share/keepsake; resetToBase improving
    // that is not a regression.)
    nav.resetToBase();
    setDiscovery(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // X / Cancel / backdrop on ANY share screen closes the WHOLE share flow:
  // requestClose on the shallowest open share layer issues one navigate(-count)
  // and the reconciler pops the whole sub-stack, firing each close() (§3.5
  // close semantics). NEVER clear the slots directly here (that would desync
  // history). Browser/hardware/swipe back steps one screen via the reconciler.
  const closeShareFlow = () => {
    const key =
      shareConfirmCardForId !== null
        ? 'share-confirm'
        : sendChoiceCardForId !== null
          ? 'share-choice'
          : shareCardForId !== null
            ? 'share-text'
            : mailCardForId !== null
              ? 'share-mail'
              : null;
    if (key) nav.requestClose(key);
  };

  const routeShareDestination = useCallback((prayerId: string) => {
    if (PICKER_ENABLED) {
      setSendChoiceCardForId(prayerId);
    } else {
      setShareCardForId(prayerId);
    }
  }, []);

  const openShareEntry = useCallback(
    (prayerId: string) => {
      if (!authorizeShare(prayerId, 'entry')) return;
      routeShareDestination(prayerId);
    },
    [authorizeShare, routeShareDestination],
  );

  // RememberStream → per-moment share. Routes through the existing
  // PICKER_ENABLED chain that Garden's airplane share already uses
  // (sendChoiceCardForId / shareCardForId / mailCardForId slots
  // mounted below). Private prayers detour through ShareConfirmDialog
  // first; non-private route directly. Matches the policy on
  // PrayerDetail's handleSendCardFromLeaf — single routing chain for
  // every outbound share.
  const handleShareMoment = (prayer: Prayer) => openShareEntry(prayer.id);

  const handleShareConfirmProceed = () => {
    // Additive (stepped share, §3.5): do NOT clear shareConfirmCardForId here.
    // The share-confirm layer stays mounted underneath the next screen so
    // browser-back steps confirm <- choice/text one at a time; X closes the
    // whole flow via closeShareFlow.
    const result = confirmShare('entry');
    if (result.status !== 'authorized') return;
    routeShareDestination(result.prayerId);
  };

  const handleShareReconfirmProceed = () => {
    const result = confirmShare('outbound');
    if (result.status !== 'authorized') return;
    nav.requestClose('share-reauthorize');
  };

  const handleShareReconfirmCancel = () => {
    nav.requestClose('share-reauthorize');
  };

  const authorizeTextOutbound = useCallback(() => {
    return shareCardForId !== null && authorizeShare(shareCardForId, 'outbound');
  }, [authorizeShare, shareCardForId]);

  const authorizeMailOutbound = useCallback(() => {
    return mailCardForId !== null && authorizeShare(mailCardForId, 'outbound');
  }, [authorizeShare, mailCardForId]);

  useEffect(() => {
    if (
      shareConfirmCardForId === null &&
      shareReconfirmCardForId === null &&
      sendChoiceCardForId === null &&
      shareCardForId === null &&
      mailCardForId === null
    ) {
      clearShareApprovedGrant();
    }
  }, [
    shareConfirmCardForId,
    shareReconfirmCardForId,
    sendChoiceCardForId,
    shareCardForId,
    mailCardForId,
    clearShareApprovedGrant,
  ]);

  // ---- Resume-card handlers (B.2) ----

  const handleContinueEnhanced = async (preloadText: string, queueItemId: string): Promise<boolean> => {
    // Pre-load PrayPage's textarea with the previous session's
    // transcribed text. The new recording's onFinal handler appends
    // with a single space (existing PrayPage onFinal pattern) per E.4
    // text-only concatenation rule. Trim whitespace per chunk before
    // joining so we don't accumulate stray spaces across multiple
    // continue cycles.
    const trimmed = preloadText.trim();
    if (!trimmed) return false;
    // DS (duplicate-save follow-through, 2026-10-20): first record that the
    // draft holds this recording's words (the existing draft-ids key), proven
    // by reading it back. If the card's close then fails and the recording is
    // offered again after a reopen, the draft's Save claims it, or refuses to
    // save its words twice (findHeldRecordingConflicts), instead of making a
    // second prayer. A failed association or draft write fails the transfer:
    // nothing moves, the association is taken back and the source is kept.
    const previousIds = [...committedInlineAudioIdsRef.current];
    persistPrayerDraftAudioIds([...previousIds, queueItemId]);
    if (!loadPrayerDraftAudioIds().includes(queueItemId)) {
      persistPrayerDraftAudioIds(previousIds);
      return false;
    }
    const merged = mergeRecoveredVoiceText(textRef.current, trimmed);
    // The durable draft write is the one-way door. Only after it succeeds may
    // ResumeRecordingCard delete the queue row that supplied these words.
    if (!persistPrayerDraft(merged)) {
      persistPrayerDraftAudioIds(previousIds);
      return false;
    }
    committedInlineAudioIdsRef.current.add(queueItemId);
    heldWordsRef.current.set(queueItemId, trimmed);
    const recoveredMode = recoveryModes[queueItemId] ?? null;
    setComposeMode(!textRef.current.trim() || composeMode === recoveredMode ? recoveredMode : null);
    setGateBypassed(true);
    setText(merged);
    return true;
  };

  // DS (2026-10-20): the resume card removed this recording (its Continue's
  // close or its Discard succeeded); the draft no longer holds it.
  const handleRecordingRemoved = (queueItemId: string) => {
    releaseHeldRecordings([queueItemId]);
  };

  const handleResumeEnhanced = () => {
    // ResumeRecordingCard calls this only after the durable draft exists AND
    // the prior queue row was removed. Keeping start in a later task lets the
    // textarea state flush before the replacement recording session opens.
    setTimeout(() => voice.start('prayer'), 0);
  };

  const handleSavePending = async (
    queueItemId: string,
    scriptureAnchor?: ScriptureAnchor,
  ): Promise<boolean | 'reopen'> => {
    // Core-content cutover: no new prayer rows. The queued audio row
    // stays exactly where it is, which is the outcome the cutover wants
    // (those bytes may be the only copy of that prayer).
    if (readOnly) return false;
    const validatedAnchor = scriptureAnchor
      ? parseScriptureAnchor(scriptureAnchor)
      : null;
    if (scriptureAnchor && !validatedAnchor) return false;
    const recoveryMode = validatedAnchor ? 'prayer' : recoveryModes[queueItemId];
    if (!recoveryMode) return false;
    // Create a Prayer with pendingTranscriptionId set; queue processor
    // fills rawText when transcription completes. Same end-state as
    // the V.1.1 inline-save claim path in handleSave, different trigger
    // (resume card vs Save button).
    const prayerId = crypto.randomUUID();
    const now = new Date().toISOString();
    const pendingPrayer: Prayer = {
      id: prayerId,
      rawText: '',
      ...entrySeed(recoveryMode, now),
      progressHistory: [],
      createdAt: now,
      updatedAt: now,
      isPrivate: defaultPrivate || undefined,
      pendingTranscriptionId: queueItemId,
      scriptureAnchor: validatedAnchor ?? undefined,
    };
    try {
      await createPendingPrayerFromAudio(pendingPrayer, queueItemId);
      try { localStorage.setItem(VISITED_KEY, '1'); } catch { /* harmless */ }
      const nextStatus: SaveStatus = getOnline() ? 'saved' : 'savedOffline';
      setStatus(nextStatus);
      setTimeout(() => setStatus(null), nextStatus === 'savedOffline' ? SAVED_OFFLINE_CONFIRM_MS : SAVED_CONFIRM_MS);
      return true;
    } catch (err) {
      console.error('[Adia] handleSavePending failed', err);
      // A stale page's duplicate "Save what I have", proven already saved by
      // another page (createPendingPrayerFromAudio's durable proof): only the
      // success presentation follows. The card closes because the proven row
      // now names its Prayer in this page's mirror; nothing is written,
      // dequeued or cleared here. Any other refusal keeps the card's own copy.
      if (err instanceof Error && err.message === PENDING_PRAYER_ALREADY_SAVED) {
        const nextStatus: SaveStatus = getOnline() ? 'saved' : 'savedOffline';
        setStatus(nextStatus);
        setTimeout(() => setStatus(null), nextStatus === 'savedOffline' ? SAVED_OFFLINE_CONFIRM_MS : SAVED_CONFIRM_MS);
        return true;
      }
      // LC8 (review LC3): the recording's bytes cannot be read in this page,
      // so trying again here cannot succeed; the card says to reopen the app.
      return saveRefusedForUnreadableRecording(err) ? 'reopen' : false;
    }
  };

  const handleKeepTyping = async (
    capturedText: string,
    scriptureAnchor?: ScriptureAnchor,
  ): Promise<boolean> => {
    // Core-content cutover: this overwrites adia-prayer-draft and clears
    // adia-interrupted-interim, two preservation-set keys. Unreachable
    // now that the resume card is not rendered while read-only; both
    // storage helpers carry their own guard as well.
    if (readOnly) return false;
    const trimmed = capturedText.trim();
    if (!trimmed) return false;
    const validatedAnchor = scriptureAnchor
      ? parseScriptureAnchor(scriptureAnchor)
      : null;
    if (validatedAnchor) {
      const now = new Date().toISOString();
      const prayer: Prayer = {
        id: crypto.randomUUID(),
        rawText: trimmed,
        parsed: null,
        status: 'Open',
        progressHistory: [],
        createdAt: now,
        updatedAt: now,
        isPrivate: defaultPrivate || undefined,
        scriptureAnchor: validatedAnchor,
      };
      try {
        // A durable Prayer commit is the one-way door for Scripture recovery.
        // ResumeRecordingCard clears the interruption only after this resolves.
        await addPrayer(prayer);
        try { localStorage.setItem(VISITED_KEY, '1'); } catch { /* harmless */ }
        const nextStatus: SaveStatus = getOnline() ? 'saved' : 'savedOffline';
        setStatus(nextStatus);
        setTimeout(
          () => setStatus(null),
          nextStatus === 'savedOffline'
            ? SAVED_OFFLINE_CONFIRM_MS
            : SAVED_CONFIRM_MS,
        );
        return true;
      } catch (err) {
        console.error('[Adia] anchored browser recovery save failed', err);
        return false;
      }
    }
    const merged = mergeRecoveredVoiceText(textRef.current, trimmed);
    // Keep the interrupted-session record until the destination draft write
    // is proven. The card dismisses (and clears it) only on a true result.
    if (!persistPrayerDraft(merged)) return false;
    setComposeMode(null);
    setGateBypassed(true);
    setText(merged);
    setTimeout(() => textareaRef.current?.focus(), 50);
    return true;
  };

  const detailPrayer = detailId ? prayers.find((p) => p.id === detailId) ?? null : null;
  const personDetailPrayer = personDetailId
    ? prayers.find((p) => p.id === personDetailId) ?? null
    : null;
  const mailCardPrayer = mailCardForId
    ? prayers.find((p) => p.id === mailCardForId) ?? null
    : null;
  const shareCardPrayer = shareCardForId
    ? prayers.find((p) => p.id === shareCardForId) ?? null
    : null;
  const sendChoiceCardPrayer = sendChoiceCardForId
    ? prayers.find((p) => p.id === sendChoiceCardForId) ?? null
    : null;
  const shareConfirmCardPrayer = shareConfirmCardForId
    ? prayers.find((p) => p.id === shareConfirmCardForId) ?? null
    : null;
  const shareReconfirmCardPrayer = shareReconfirmCardForId
    ? prayers.find((p) => p.id === shareReconfirmCardForId) ?? null
    : null;
  const hasPrayers = prayers.length > 0;

  // NavStack layer registrations (A1). Each returns the computed z-index for
  // its overlay root (undefined while closed). All booleans are false at
  // PrayPage mount, so each registers closed -> StrictMode-safe (§3.2a).
  const zGarden = useNavLayer('garden', gardenOpen, closeGarden);
  const zRemember = useNavLayer('remember', summaryOpen, () => setSummaryOpen(false));
  const zEvidence = useNavLayer('evidence', evidenceOpen, () => setEvidenceOpen(false));
  const zDetail = useNavLayer('detail', detailId !== null, handleDetailClose);
  const zKeepsake = useNavLayer('keepsake', keepsakeFor !== null, () => setKeepsakeFor(null));
  // A3: `person` (above its opener) + the DISTINCT `person-detail` (above
  // person). Both false at mount -> StrictMode-safe. `person-update` registers
  // inside the extracted PersonView (the only owner mounting with it closed).
  const zPerson = useNavLayer('person', personFor !== null, closePerson);
  const zPersonDetail = useNavLayer('person-detail', personDetailId !== null, personDetailClose);
  const zShareConfirm = useNavLayer(
    'share-confirm',
    shareConfirmCardPrayer !== null,
    clearShareEntryRequest,
  );
  const zShareReconfirm = useNavLayer(
    'share-reauthorize',
    shareReconfirmCardPrayer !== null,
    clearShareOutboundRequest,
  );
  const zShareChoice = useNavLayer(
    'share-choice',
    sendChoiceCardPrayer !== null,
    () => setSendChoiceCardForId(null),
  );
  const zShareText = useNavLayer('share-text', shareCardPrayer !== null, () =>
    setShareCardForId(null),
  );
  const zShareMail = useNavLayer('share-mail', mailCardPrayer !== null, () =>
    setMailCardForId(null),
  );

  useLayoutEffect(() => {
    if (shareConfirmCardForId !== null && shareConfirmCardPrayer === null) {
      clearShareEntryRequest();
    }
    if (shareReconfirmCardForId !== null && shareReconfirmCardPrayer === null) {
      clearShareOutboundRequest();
    }
    if (sendChoiceCardForId !== null && sendChoiceCardPrayer === null) {
      setSendChoiceCardForId(null);
    }
    if (shareCardForId !== null && shareCardPrayer === null) {
      setShareCardForId(null);
    }
    if (mailCardForId !== null && mailCardPrayer === null) {
      setMailCardForId(null);
    }
  }, [
    shareConfirmCardForId,
    shareConfirmCardPrayer,
    shareReconfirmCardForId,
    shareReconfirmCardPrayer,
    clearShareEntryRequest,
    clearShareOutboundRequest,
    sendChoiceCardForId,
    sendChoiceCardPrayer,
    shareCardForId,
    shareCardPrayer,
    mailCardForId,
    mailCardPrayer,
  ]);

  // --- A3 stale-layer guards (rule #78 §3.4 / §3.4b) ---
  // A `detail` / `person-detail` layer is registered off its *Id state, but its
  // PrayerDetail mount renders off the derived prayer. If the prayer disappears
  // (a same-prayer delete from person-detail, or a cross-device sync deletion)
  // the visible detail unmounts but the LAYER stays in the stack -> the next
  // Back would burn an invisible layer. Two guards close the dead layer before
  // paint. They no-op under popInFlight, so they never double-pop or fight a
  // deliberate delete.

  // Top-level `detail`: can ONLY be popped once it is the TOP layer (popping it
  // mid-stack would wrongly pop the `person` above it). detailIsTop re-fires the
  // effect on the person-pop transition (§3.2b subscribe). nav is stable.
  const detailIsTop = useSyncExternalStore(nav.subscribe, () => nav.isTopLayer('detail'));
  useLayoutEffect(() => {
    if (
      detailId !== null &&
      detailIsTop &&
      !prayers.some((p) => p.id === detailId) &&
      nav.has('detail')
    ) {
      nav.requestClose('detail');
    }
  }, [detailId, detailIsTop, prayers, nav]);

  // `person-detail`: nothing legitimate ever sits above it except its OWN
  // orphaned children, so pop the whole sub-stack immediately (no top gate).
  // navLen re-fires the effect after a child above it pops in the rare
  // external-delete-while-child-open case (the child's cleanup wins popInFlight
  // first; once it clears, the length change re-runs this and it pops cleanly).
  const navLen = useSyncExternalStore(nav.subscribe, () => nav.length());
  useLayoutEffect(() => {
    if (
      personDetailId !== null &&
      personDetailPrayer === null &&
      nav.has('person-detail')
    ) {
      nav.requestClose('person-detail');
    }
  }, [personDetailId, personDetailPrayer, navLen, nav]);

  const entryReady = draftReady && audioHydrated && hydration.state === 'ready';
  // Adjust-state-on-change in render (as pendingModeShown): the first entry-ready
  // render ends start-up for this mount.
  if (entryReady && !startupSettled) setStartupSettled(true);
  // A delete's Undo window and a save confirmation are things the user must be
  // able to see and act on; the welcome waits for them like every other notice.
  const gateSafe = !readOnly && !displayValue.trim() && !isSaving && !isListening && !isWriting &&
    !voice.inlineSaveAvailable && !resumeCardOpen && deferredRecordingCount === 0 &&
    pendingAudioCount === 0 && !prayers.some(prayer => Boolean(prayer.pendingTranscriptionId)) && navLen === 0 &&
    !saveReopen && !heldConflict && !(noticeStack?.naturalHeight) && !infoNotice && !failureNotice &&
    recentlyDeleted === null && status === null;
  const welcomeEligible = gateSafe && !gateBypassed && composeMode === null;
  // Readiness gates the first appearance. Once presented, a technical refresh
  // must not replace a focused button between native keydown and keyup.
  // Pending work and actionable notices always escape immediately.
  const showWelcome = welcomeEligible && (gatePresented || (entryReady && !isInitialSyncing));
  useLayoutEffect(() => {
    setGatePresented(showWelcome);
  }, [showWelcome]);
  // The leaving fade finishes on its own (pointer-transparent, never blocking):
  // a first keystroke inside it must not cut the confirmation short.
  const showGate = gateLeaving || (gateSafe && showWelcome);
  // Only an unanswered welcome holds Home. Once a choice is made the composer
  // is live underneath while the welcome fades away above it.
  const gateBlocking = gateSafe && showWelcome;
  const showEntryMode = !gateBlocking && !readOnly && !isListening && !isWriting;
  // Start-up presentation only, no gate decision: until this mount has read
  // its draft and audio and the prayer corpus is loaded (the first load, or the
  // reload a return to Home starts, as from Settings or the Bible), a Home still
  // on course for the welcome holds back the composer pieces the welcome hides
  // (PrayPage.module.css), so it never opens over a flash of them. A reload
  // while the welcome or a choice already shows never holds anything: then
  // showGate is set or welcomeEligible is not.
  const welcomeExpected = welcomeEligible && !showGate && !startupSettled &&
    (!draftReady || !audioHydrated || hydration.state !== 'ready');
  // The fail-safe ends start-up too. Its clock starts when the hold first
  // appears and never restarts, so after a failed or stalled first read the
  // hold cannot return once the pieces have faded in.
  if (welcomeExpected && !startupHoldSeen) setStartupHoldSeen(true);
  useEffect(() => {
    if (!startupHoldSeen || startupSettled) return;
    const timer = window.setTimeout(() => setStartupSettled(true), WELCOME_EXPECTED_FAIL_SAFE_MS);
    return () => window.clearTimeout(timer);
  }, [startupHoldSeen, startupSettled]);
  // Keep the two choices clear of the garden pill, which stays reachable
  // beneath them. Layout offsets only (the pill's entrance moves a transform).
  const [gateReserve, setGateReserve] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!showGate) { setGateReserve(null); return; }
    const container = containerRef.current;
    const dock = dockRef.current;
    if (!container || !dock) { setGateReserve(null); return; }
    const measure = () => {
      const actions = homeActionStackRef.current;
      if (!actions) { setGateReserve(0); return; }
      const bottom = container.getBoundingClientRect().bottom;
      const actionsTop = dock.getBoundingClientRect().top + actions.offsetTop;
      setGateReserve(Math.max(0, Math.ceil(bottom - actionsTop)));
    };
    const sizes = new ResizeObserver(measure);
    sizes.observe(container);
    sizes.observe(dock);
    if (homeActionStackRef.current) sizes.observe(homeActionStackRef.current);
    measure();
    return () => sizes.disconnect();
  }, [showGate, hasPrayers, discovery, isInitialSyncing]);
  const feedbackLimitPx = showEntryMode && composerLayout.active
    ? composerLayout.feedbackBottom - composerLayout.feedbackTop
    : ordinaryFeedbackLimitPx;
  useLayoutEffect(() => {
    const container = containerRef.current;
    const dock = dockRef.current;
    const actionsBand = actionsBandRef.current;
    const header = container?.querySelector<HTMLElement>(`.${styles.settingsLink}`);
    const feedback = undoSlot?.closest<HTMLElement>('[data-home-feedback]');
    if (!container || !dock || !actionsBand || !header || !feedback) return;
    const measure = () => {
      const containerTop = container.getBoundingClientRect().top;
      const headerBottom = header.getBoundingClientRect().bottom - containerTop + container.scrollTop;
      const feedbackTop = headerBottom + parseFloat(getComputedStyle(container).getPropertyValue('--home-feedback-gap'));
      const dockBottom = containerHeight - dock.offsetHeight - 8;
      const stackTop = noticeStack ? noticeStack.top - containerTop : 0;
      const stackHeight = noticeStack?.naturalHeight ?? 0;
      const hasColumn = feedback.hasAttribute('data-column');
      const filled = feedback.hasAttribute('data-filled');
      const feedbackStyle = getComputedStyle(feedback);
      const padding = filled ? parseFloat(feedbackStyle.paddingTop) + parseFloat(feedbackStyle.paddingBottom) : 0;
      // Semantic occupancy and uncapped Undo height only. The readable band
      // allows a 44px control plus the column's existing two 20px scroll fades.
      // Never derive this requirement from capped feedback, mode or textarea.
      const feedbackNeed = padding + undoRowHeight + (hasColumn ? 44 + 2 * 20 : 0)
        + (hasColumn && undoRowHeight > 0 ? 8 : 0);
      const stackMinimum = stackHeight > 0 ? Math.min(48, stackHeight) + 8 : 0;
      if (showEntryMode) {
        // Three flow bands: stationary header/notices, scrollable composer,
        // and lower actions. Only independent viewport/chrome/actions and
        // uncapped priority minima determine the notice cap and fallback.
        const vv = window.visualViewport;
        const inset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
        // As the ordinary branch below: record the resting height only while
        // the visual viewport is unobstructed, so a resize fired partway
        // through the keyboard's animation (inset still under the 100 px
        // keyboard threshold) is never kept as the resting height and the
        // stack cannot shrink under an overlay's focused field.
        if (!keyboardOpen && inset < 2) restingComposerHeightRef.current = containerHeight;
        const height = homeCovered && keyboardOpen
          ? restingComposerHeightRef.current ?? containerHeight : containerHeight;
        const visibleTop = keyboardOpen && !homeCovered
          ? Math.max(0, (vv?.offsetTop ?? 0) - containerTop) : 0;
        let chromeBottom = Math.max(headerBottom, visibleTop);
        const status = topStatusRef.current;
        for (const child of status?.children ?? []) {
          if (child instanceof HTMLElement && child.offsetHeight > 0) {
            chromeBottom = Math.max(chromeBottom,
              child.getBoundingClientRect().bottom - containerTop + container.scrollTop);
          }
        }
        const minimumFeedback = filled
          ? Math.max(0, feedbackTop - chromeBottom) + feedbackNeed : 0;
        const wholePage = height - Math.max(chromeBottom, stackTop + stackMinimum)
          < actionsBand.offsetHeight + minimumFeedback + 44 + 8;
        // The global stack retains its owner/slots. Reserve the existing
        // pinned Undo/readable column minimum and a usable composer band.
        const stackRoom = height - (wholePage ? 0 : actionsBand.offsetHeight)
          - feedbackNeed - 44 - stackTop - 8;
        const bandCap = stackHeight > stackRoom ? Math.max(48, Math.floor(stackRoom)) : null;
        // Home's own notice bound (v0.2.9, LS3 / NA17 / NA18) is a floor on the
        // cap whatever the three bands choose, including the whole-page
        // fallback: the keyboard limit, else 8 px above the dock, minus Undo's
        // whole row (the Clear pill does not float over the input in this
        // layout; its row belongs to the actions band counted above). While an
        // overlay covers Home with the keyboard open, the last keyboard-closed
        // bound is kept (see the ordinary branch below) so the stack does not
        // resize under the overlay's focused field.
        const liveHomeBottom = keyboardOpen ? containerHeight - 8 : dockBottom;
        if (!keyboardOpen && inset < 2) restingBottomRef.current = liveHomeBottom;
        const homeBottom = homeCovered && keyboardOpen && restingBottomRef.current !== null
          ? restingBottomRef.current : liveHomeBottom;
        const homeRoom = homeBottom - (undoRowHeight > 0 ? undoRowHeight + 8 : 0) - stackTop;
        const homeCap = stackHeight > homeRoom ? Math.max(48, Math.floor(homeRoom - 8)) : null;
        const cap = bandCap === null ? homeCap : homeCap === null ? bandCap : Math.min(bandCap, homeCap);
        const clearance = stackHeight > 0
          ? Math.ceil(stackTop + (cap === null ? stackHeight : cap + 8)) : 0;
        setNoticeStackCap(stackHeight > 0 ? cap : null);
        setNoticeClearance(clearance);
        const top = Math.ceil(Math.max(chromeBottom, clearance));
        const contentFeedbackTop = Math.max(feedbackTop, clearance);
        // The feedback shell is a flow sibling, never a descendant of the
        // composer scroller. Its existing column handles long notice content.
        // In exceptionally short viewports the complete page scrolls instead
        // of shrinking Undo, the input band or the unchanged lower actions.
        const feedbackBottom = Math.max(
          height - (wholePage ? 0 : actionsBand.offsetHeight) - 44 - 8,
          contentFeedbackTop + feedbackNeed,
        );
        const next = { active: true, top, height: Math.max(44, height), wholePage,
          feedbackTop: contentFeedbackTop, feedbackBottom };
        setComposerLayout(current => Object.keys(next).every(key =>
          current[key as keyof typeof current] === next[key as keyof typeof next]) ? current : next);
        return;
      }
      setComposerLayout(current => current.active ? { ...current, active: false } : current);
      const needsShortHeight = showEntryMode && (filled || stackHeight > 0)
        && dockBottom < Math.max(feedbackTop, stackTop + stackMinimum) + feedbackNeed;
      // On short screens the unchanged dock follows the composer in normal
      // flow. Do not subtract it from the feedback viewport, or use Clear's
      // textarea-derived position to expand this independent fallback budget.
      const liveBottom = needsShortHeight ? containerHeight - 8 : ordinaryFeedbackLimitPx ?? dockBottom;
      const vv = window.visualViewport;
      const inset = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      if (!keyboardOpen && inset < 2) {
        restingBottomRef.current = liveBottom;
        restingShortHeightRef.current = needsShortHeight;
      }
      const keepResting = homeCovered && keyboardOpen && restingBottomRef.current !== null
        && (showEntryMode || !restingShortHeightRef.current);
      const bottom = keepResting ? restingBottomRef.current! : liveBottom;
      const shortHeight = showEntryMode && (keepResting ? restingShortHeightRef.current : needsShortHeight);
      if (stackHeight <= 0) {
        setNoticeStackCap(null);
        setNoticeClearance(0);
        return;
      }
      // Share exactly the same fallback bottom with HomeFeedbackArea. Undo
      // stays pinned; the existing column scrolls within its remaining room.
      // Extremely small viewports cannot show all minima simultaneously; do
      // not manufacture extra viewport height or shrink/hide the pinned row.
      const reserve = shortHeight ? feedbackNeed : undoRowHeight > 0 ? undoRowHeight + 8 : 0;
      const room = bottom - reserve - stackTop;
      const cap = stackHeight > room ? Math.max(48, Math.floor(room - 8)) : null;
      setNoticeStackCap(cap);
      setNoticeClearance(Math.ceil(stackTop + (cap === null ? stackHeight : cap + 8)));
    };
    const sizes = new ResizeObserver(measure);
    sizes.observe(container); sizes.observe(dock); sizes.observe(header);
    if (showEntryMode) sizes.observe(actionsBand);
    const topStatus = topStatusRef.current;
    const observedStatus = new Set<Element>();
    const observeStatus = () => {
      for (const child of observedStatus) {
        if (!topStatus?.contains(child)) { sizes.unobserve(child); observedStatus.delete(child); }
      }
      for (const child of topStatus?.children ?? []) {
        if (!observedStatus.has(child)) { sizes.observe(child); observedStatus.add(child); }
      }
      measure();
    };
    const statusChanges = new MutationObserver(observeStatus);
    if (topStatus) {
      statusChanges.observe(topStatus, { childList: true, subtree: true, characterData: true });
      topStatus.addEventListener('animationend', measure);
    }
    const occupancy = new MutationObserver(measure);
    occupancy.observe(feedback, { attributes: true, attributeFilter: ['data-filled', 'data-column', 'data-pinned'] });
    observeStatus();
    return () => {
      sizes.disconnect(); occupancy.disconnect(); statusChanges.disconnect();
      topStatus?.removeEventListener('animationend', measure);
    };
  }, [noticeStack, containerHeight, ordinaryFeedbackLimitPx, undoRowHeight, keyboardOpen, homeCovered, undoSlot, showEntryMode]);
  // The guidance sits 12px under the mode row. Both live in .upperContent (the
  // guidance's containing block whenever .composerWithMode applies, the only
  // time its top is set) and scroll together, so only the row's own size can
  // move it: measure in the commit that mounts the row and on the row's own
  // resizes, never on viewport, keyboard or notice-layout changes.
  useLayoutEffect(() => {
    const row = entryModeRef.current;
    const origin = upperContentRef.current;
    if (!showEntryMode || !row || !origin) { setEntryModeBottom(0); return; }
    const measure = () => setEntryModeBottom(Math.ceil(
      row.getBoundingClientRect().bottom - origin.getBoundingClientRect().top));
    const sizes = new ResizeObserver(measure);
    sizes.observe(row);
    measure();
    return () => sizes.disconnect();
  }, [showEntryMode]);

  return (
    <div ref={containerRef}
      className={`${styles.container} ${showEntryMode ? styles.composerWithMode : ''} ${showEntryMode && composerLayout.wholePage ? styles.composerWholePage : ''}`}
      style={{
        ...(showEntryMode && composerLayout.active ? {
          '--home-visible-composer-height': `${composerLayout.height}px`,
          '--home-upper-top': `${composerLayout.top}px`,
        } : {}),
        ...(showGate && gateReserve !== null ? { '--entry-gate-reserve': `${gateReserve}px` } : {}),
      } as CSSProperties}
      data-entry-choice-state={entryReady ? showGate ? 'welcome' : 'composer' : 'preparing'}
      data-entry-gate={showGate ? gateBlocking ? 'open' : 'leaving' : undefined}
      data-entry-welcome-expected={welcomeExpected ? '' : undefined}>
      <div className={styles.homeContents} inert={gateBlocking} aria-hidden={gateBlocking || undefined}>
      {/* Tree-by-stream atmosphere behind all chrome (full-bleed on desktop). */}
      <HomePrayerScene
        active={isListening || isWriting}
        saved={status !== null}
        hasText={!!displayValue}
      />
      {/* Core-content cutover: the read-only maintenance state, in the
          same top-center slot family as the pills below. Takes the slot
          exclusively: while read-only, no new audio can be queued and
          neither of the two pills has anything to say that matters more
          than "your prayers are protected". */}
      <div ref={topStatusRef} className={styles.homeContents}>
      {readOnly && <MaintenanceBanner />}
      {/* B.2 top-center pending pill — auto-shows when audio queue has
          pending entries, auto-hides on drain. No collision with the
          settings gear (top-right) or copy area (top-left). */}
      {!readOnly && <PendingTranscriptionPill />}
      {/* Quiet offline indicator (Chunk 4, §5.3). Same top-center slot as the
          pending pill, mutually exclusive with it (renders only while offline
          AND no audio is queued), self-hides on reconnect. */}
      {!readOnly && <OfflineIndicator />}
      </div>
      </div>
      {/* The Bible stays reachable while the welcome asks (like the garden
          pill, outside every inert wrapper; painted above the scrim by the
          CSS). Its caption shows only then; the visible words are its name. */}
      {BIBLE_ENABLED && (
        <Link to="/bible" className={styles.bibleLink} aria-label={t(gateBlocking ? 'pray.readBible' : 'bible.openLabel')}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
          </svg>
          {showGate && (
            <span className={styles.bibleWelcome} aria-hidden="true">
              <span className={styles.bibleGlass} />
              <span className={styles.bibleCaption}>{t('pray.readBible')}</span>
            </span>
          )}
        </Link>
      )}
      <div className={styles.homeContents} inert={gateBlocking} aria-hidden={gateBlocking || undefined}>

      <Link to="/settings" className={styles.settingsLink} aria-label={t('detail.settingsLabel')}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </Link>

      </div>
      <div className={styles.noticeBand}
        style={showEntryMode && composerLayout.active ? {
          '--home-feedback-inset': `${Math.max(0, composerLayout.feedbackTop - composerLayout.top)}px`,
          '--home-feedback-cap': `${composerLayout.feedbackBottom - composerLayout.feedbackTop}px`,
        } as CSSProperties : undefined}>
      {/* Home feedback area (garden-remember readiness P1, 2026-10-15): one
          bounded column below the header for delete Undo (pinned), protection
          messages and the recovery UI, instead of separate absolute positions
          that covered each other. Scroll order: a failure message (head slot,
          from DeletionNotices), the rescue banner, ContentLifecycleNotice, a
          pending / preparing message (tail slot). Bounded above the dock, the
          Clear pill, or the keyboard. Presentation only. */}
      <HomeFeedbackArea
        label={t('pray.feedbackRegionAria')}
        visibleHeight={containerHeight}
        limitPx={feedbackLimitPx}
        noticeClearancePx={showEntryMode && composerLayout.active ? 0 : noticeClearance}
        undoSlotRef={setUndoSlot}
        headSlotRef={setHeadSlot}
        tailSlotRef={setTailSlot}
      >
        {/* V.B.3 inline-save rescue banner. Surfaces when an
            awaitingInlineSave orphan is parked in IDB AND the textarea
            is empty AND no recording is active. Hides reactively on
            typing, mic tap, Save (orphan claim), or Discard. Per the
            V.B.3 deriveCounters change, the orphan is no longer counted
            by PendingTranscriptionPill, so the two surfaces never
            double-represent the same audio. */}
        {/* LC10 item 3: the reopen refusal of a Save. Announced, wraps, never
            timed or faded; it leaves when the person acts (see saveReopen). */}
        {!readOnly && saveReopen !== null && (
          <p className={styles.saveReopenAlert} role="alert">
            {t(saveReopen === 'words' ? 'voice.saveNeedsReopen' : 'voice.saveRecordingNeedsReopen')}
          </p>
        )}
        {/* RU item 3: recordings set aside by the resume card's "Not now".
            Says they are safe and how to save them; nothing to tap. */}
        {!readOnly && deferredRecordingCount > 0 && (
          <div ref={deferredEntryRef} className={styles.deferredRecording} role="status" tabIndex={-1}>
            {deferredWaitingCount > 0 && (
              <>
                <p className={styles.deferredRecordingTitle}>
                  {t('voice.resumeDeferredTitle', { count: deferredWaitingCount })}
                </p>
                <p className={styles.deferredRecordingBody}>
                  {t('voice.resumeDeferredBody', { count: deferredWaitingCount })}
                </p>
              </>
            )}
            {deferredMovedCount > 0 && (
              <>
                <p className={styles.deferredRecordingTitle}>
                  {t('voice.resumeDeferredMovedTitle', { count: deferredMovedCount })}
                </p>
                <p className={styles.deferredRecordingBody}>
                  {t('voice.resumeDeferredMovedBody', { count: deferredMovedCount })}
                </p>
              </>
            )}
          </div>
        )}
        {!readOnly && voice.inlineSaveAvailable && !text.trim() && !isListening && !isWriting && (
          <>
          {homeOfferId && !homeOfferReference && <EntryModeChoice
            choiceRef={recoveryChoiceRef} value={recoveryModes[homeOfferId] ?? null}
            required={requiredRecoveryId === homeOfferId && !recoveryModes[homeOfferId]}
            onChoose={mode => setRecoveryModes(current => ({ ...current, [homeOfferId]: mode }))} />}
          <InlineSaveRescueBanner
            parked={!enhancedVoiceEnabled}
            onDiscard={handleRescueDiscard}
            scriptureReference={homeOfferReference}
            capturedAtLabel={homeOfferCapturedAt ? formatShortDateTime(homeOfferCapturedAt, i18n.language) : null}
            heardNoWords={homeOfferHeardNoWords}
            failedWithoutWords={homeOfferFailedWithoutWords}
            onSave={voice.inlineSaveSavable ? () => { void handleSave(); } : undefined}
          />
          </>
        )}
        {!readOnly && <ContentLifecycleNotice />}
      </HomeFeedbackArea>
      </div>

      {/* The welcome is rendered here, before the composer and the dock, so the
          keyboard order is the Bible corner, Praise, Pray, then the garden pill
          (the two pieces of Home that are not inert while it asks). It is
          positioned over the whole container; DOM order only governs focus, not
          paint (z-index 30). */}
      {showGate && <PraiseOrPrayGate selected={gateLeaving ? composeMode : null}
        onChoose={chooseGateMode} onExited={finishGateTransition} />}
      <div ref={upperViewportRef} className={styles.upperViewport} data-home-upper-scroll="">
      <div ref={upperContentRef} className={styles.upperContent}>
      <div className={styles.homeContents} inert={gateBlocking} aria-hidden={gateBlocking || undefined}>
      {showEntryMode && (
        <div ref={entryModeRef} className={styles.entryMode}>
          <EntryModeChoice value={composeMode} onChoose={chooseMode} required={modeRequired}
            disabled={capturedMode !== null || isSaving} choiceRef={modeChoiceRef} />
          {showPendingMode && capturedMode && <p role="status">{t('entry.pendingMode', { mode: t(`entry.${capturedMode}Label`) })}</p>}
        </div>
      )}
      {/* The top text-dissolve is a mask on the textarea (see .textarea in the
          CSS), so the prayer text fades into the real scene image instead of
          behind an opaque fade bar. No overlay element is needed. */}
      {/* Core-content cutover: `readOnly`, deliberately NOT `disabled`. A
          disabled textarea cannot be selected in Safari, and a
          pre-cutover draft sitting in this field may be the only copy of
          that prayer, so the user has to be able to select and copy it.
          readOnly keeps selection and copy while refusing edits, which
          also stops the draft-persistence effect above from ever seeing
          a changed value. */}
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        // Reserve the complete empty guidance, including both subtitle lines.
        // The unchanged dock supplies clearance below this content; typing
        // restores the ordinary 96px minimum and natural composer scrolling.
        style={showEntryMode && !displayValue && !isSaving
          ? { minHeight: Math.max(96, 12 + guidanceHeight), flexBasis: Math.max(96, 12 + guidanceHeight) }
          : undefined}
        value={displayValue}
        onChange={handleChange}
        placeholder=" "
        aria-label={t(composeMode === 'praise' ? 'entry.praiseInputAria' : 'pray.prayerInputAria')}
        disabled={isSaving}
        readOnly={readOnly}
        data-update-safe-draft=""
      />

      {!displayValue && !isSaving && (
        <div ref={setGuidanceElement} className={styles.placeholder} aria-hidden="true" data-home-compose-guidance=""
          style={showEntryMode ? { top: entryModeBottom + 12 } : undefined}>
          <span className={styles.placeholderMain}>{t(composeMode === 'praise' ? 'entry.praisePlaceholder' : 'pray.placeholder')}</span>
          <div className={styles.placeholderSubGroup}>
            <span className={styles.placeholderSubLine}>{t('pray.placeholderSubLine1')}</span>
            <span className={styles.placeholderSubLine}>{t('pray.placeholderSubLine2')}</span>
          </div>
        </div>
      )}


      </div>
      </div>
      </div>
      <div className={styles.homeContents} inert={gateBlocking} aria-hidden={gateBlocking || undefined}>
      {!readOnly && confirmingClear && (
        <div
          className={styles.clearConfirm}
          style={
            clearConfirmationTop !== null
              ? {
                  // Anchor the dialog's bottom edge to where the Clear pill
                  // sat, so the confirmation appears right where the user
                  // just tapped. Clamped to the upper half of the container
                  // so it never overlaps bottom chrome (mic, Enhanced Voice
                  // label, Save button) — simpler and more robust than
                  // measuring each chrome element individually. Dialog
                  // extends upward from the anchor via translateY(-100%).
                  top: `${clearConfirmationTop}px`,
                  transform: 'translateY(-100%)',
                }
              : undefined
          }
        >
          <p className={styles.clearConfirmText}>{t('pray.clearConfirm')}</p>
          <div className={styles.clearConfirmButtons}>
            <button
              type="button"
              className={styles.clearKeep}
              onClick={() => setConfirmingClear(false)}
            >
              {t('pray.keepIt')}
            </button>
            <button
              type="button"
              className={styles.clearAction}
              onClick={() => {
                if (isListening) cancelVoice();
                setText('');
                setStatus(null);
                setSaveReopen(null);
                setHeldConflict(null);
                // DS fix round 1 (REVIEW-DS1 F2): the cleared words were the
                // only reason to hold these recordings; release them all.
                releaseHeldRecordings([...committedInlineAudioIdsRef.current]);
                setConfirmingClear(false);
                setTimeout(() => textareaRef.current?.focus(), 50);
              }}
            >
              {t('pray.clearAction')}
            </button>
          </div>
        </div>
      )}

      </div>
      <div ref={actionsBandRef} className={styles.actionsBand}>
      <div className={styles.homeContents} inert={gateBlocking} aria-hidden={gateBlocking || undefined}>
      {!readOnly && (
        <div
          ref={clearAreaRef}
          className={`${styles.clearArea} ${showClear && clearTop !== null ? styles.clearVisible : ''}`}
          style={!showEntryMode && clearTop !== null ? { top: `${clearTop}px` } : undefined}
          aria-hidden={!showClear}
        >
          <ClearButton
            onClick={() => {
              const root = containerRef.current;
              const area = clearAreaRef.current;
              if (root && area) {
                const rootTop = root.getBoundingClientRect().top;
                const vv = window.visualViewport;
                const visibleTop = (vv?.offsetTop ?? 0) - rootTop + root.scrollTop;
                const visibleHeight = vv?.height ?? window.innerHeight;
                setClearConfirmationTop(Math.min(
                  area.getBoundingClientRect().top - rootTop + root.scrollTop + 32,
                  visibleTop + visibleHeight * 0.5,
                ));
              }
              setConfirmingClear(true);
            }}
            label={t('pray.clear')}
          />
        </div>
      )}
      </div>

      {/* Dock band — bottom-of-page zone holding the mic, contextual
          mid-slot label (tap-to-write OR keep-praying OR listening /
          writing satellites), and primary save / home-action chrome.
          Flex sibling of the textarea so the textarea is structurally
          bounded above this dock — text physically cannot reach the
          mic zone. See rule #18 for the historical context behind
          the dock split. */}
      <div ref={dockRef} className={styles.dock}>
        <div className={styles.homeContents} inert={gateBlocking} aria-hidden={gateBlocking || undefined}>
        {voice.isSupported && !readOnly && heldConflict === null && (
          <div className={styles.micWrap}>
            <MicButton
              size="xl"
              showStopCueInside
              isListening={isListening}
              isTranscribing={isWriting}
              disabled={isSaving}
              onClick={toggleMic}
              ariaLabel={isListening ? t('detail.stopRecording') : t('detail.voiceInput')}
            />
          </div>
        )}

        {!readOnly && !isListening && !isWriting && !isSaving && !displayValue.trim() && (
          <div className={styles.tapToWriteWrap}>
            <button
              type="button"
              className={styles.tapToWriteText}
              onClick={() => textareaRef.current?.focus()}
              aria-label={t('pray.tapToWriteAria')}
            >
              {t('pray.tapToWrite')}
            </button>
          </div>
        )}

        {/* Keep-praying label — persistent invitation when there's text
            in the textarea and we're in idle compose state. Suppressed
            during the 5 s save-cue window so the saveHint above the
            save button has the visual stage to itself; the label
            returns the moment the cue clears. Tapping fires the same
            toggleMic handler as the mic, giving voice-first users a
            second tap target. */}
        {!readOnly && voice.isSupported && !isListening && !isWriting && !isSaving && !showSaveCue && heldConflict === null && displayValue.trim() && (
          <div className={styles.keepPrayingWrap}>
            <button
              type="button"
              className={styles.keepPrayingText}
              onClick={toggleMic}
            >
              {t('pray.keepPraying')}
            </button>
          </div>
        )}

        {isListening && (
          <>
            {/* Hero waveform — 260px × 48px mirrored continuous sine
                (Variant 2 / live-pulse: amplitude scales with current
                RMS via scaleY on the wrapping group; no rolling history
                buffer). Bespoke component for the home view; AudioMeter
                continues to serve cross-surface labelPills via MicButton.
                The closed-eyes trust mechanism per B.1: synthetic-mode
                keyframe keeps the wave breathing even when no audio
                source is wired so the user knows the session is alive. */}
            <div className={styles.listeningWave} aria-hidden="true">
              <HeroWaveform getLevel={voice.getLevel} />
            </div>
            <div className={styles.listeningStatus} aria-live="polite">
              <span className={styles.listeningStatusLine}>
                <span className={styles.listeningStatusDot} aria-hidden="true" />
                <span className={styles.listeningStatusText}>
                  {!enhancedVoiceEnabled
                    ? t('voice.fallbackStandard')
                    : fallbackReason === 'offline'
                    ? t('voice.fallbackOffline')
                    : fallbackReason === 'service'
                    ? t('voice.fallbackService')
                    : fallbackReason === 'unauthenticated'
                    ? t('voice.fallbackUnauthenticated')
                    : t('pray.listeningPrayer')}
                </span>
              </span>
            </div>
          </>
        )}

        {isWriting && (
          <>
            {/* Single quiet pulsing gold dot in the same vertical slot
                the waveform occupied during listening — visual continuity
                with a clean state transition (the work has shifted from
                your speaking to my processing). 1.4s cycle is short
                enough to handle very-fast (~600ms) transcription windows
                without dismounting mid-cycle. Bottom row is label-only;
                the middle dot carries the activity signal. */}
            <div className={styles.writingPulse} aria-hidden="true">
              <span className={styles.writingPulseDot} />
            </div>
            <div className={styles.writingStatus} aria-live="polite">
              <span>{t('voice.writing')}</span>
            </div>
          </>
        )}

        {/* DS (2026-10-20; placement, fix round 1 F4): the typed Save saved
            nothing because a recording whose words this composer holds was
            saved, or may have been saved, as a prayer elsewhere. It sits in
            the dock, in the mic's place and above Save, so the person's words
            above stay in view and both actions are visible at rest at every
            supported size (the feedback column is too short at 375x559).
            Announced and persistent; cleared when the person acts. */}
        {!readOnly && heldConflict !== null && (
          <div className={styles.heldConflict}>
            <p ref={heldConflictRef} className={styles.heldConflictText} role="alert" tabIndex={-1}>
              {t(heldConflict.kind === 'saved' ? 'voice.heldRecordingSaved' : 'voice.heldRecordingUnknown')}
            </p>
            <button
              type="button"
              className={styles.heldConflictAction}
              onClick={handleSaveSeparate}
              disabled={isSaving}
            >
              {t('voice.saveSeparatePrayerAction')}
            </button>
            <button
              type="button"
              className={styles.heldConflictKeep}
              onClick={handleKeepEditing}
              disabled={isSaving}
            >
              {t('voice.heldRecordingKeepEditing')}
            </button>
          </div>
        )}

        {/* Save button stays hidden until there's actually something to save.
            Reveals the moment typed text exists, interim STT starts streaming
            (browser mode), or transcription has appended final text (enhanced
            mode). Hidden during listening/writing because recording isn't a
            "ready to save" state and the bottom pill needs that slot —
            reappears the moment the user stops the mic. Keeps visible during
            the brief save-in-flight window so the "Saving..." state renders. */}
        {/* Mic repair F2: the dock's Save is for typed words; an offered
            recording's Save sits in its banner (InlineSaveRescueBanner). */}
        {!readOnly && (displayValue.trim() || isSaving) && !isListening && !isWriting && (
          <>
            {showSaveCue && !isSaving && (
              <div className={styles.saveHint} aria-hidden="true">
                {t('pray.saveHint')}
              </div>
            )}
            <button
              type="button"
              className={`${styles.saveButton} ${showSaveCue && !isSaving ? styles.saveButtonCue : ''}`}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? t('pray.saving') : t(composeMode === 'praise' ? 'entry.savePraise' : 'pray.save')}
            </button>
          </>
        )}

        {isInitialSyncing && !text.trim() && !isSaving && !isListening && !isWriting && (
          <div className={styles.gatheringArea}>
            <GatheringIndicator />
          </div>
        )}

        </div>
        {/* Mic repair F2 (2026-10-20): the Garden stays available while a
            recording is offered; the banner and its Discard stay too. While
            the welcome asks Praise or Pray, this pill and the Bible corner are
            the pieces of Home that are neither inert nor blurred: they paint
            above the scrim. */}
        {!text.trim() && !isSaving && !isListening && !isWriting && hasPrayers && !discovery && !isInitialSyncing && (
          <div ref={homeActionStackRef} className={styles.homeActionStack}>
            <button
              type="button"
              className={styles.gardenButton}
              onClick={openGarden}
              aria-label={t('pray.gardenButtonTitle')}
            >
              <svg
                className={styles.gardenButtonLeaf}
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
              </svg>
              <span className={styles.gardenButtonText}>
                <span className={styles.gardenButtonLabel}>{t('pray.gardenButtonTitle')}</span>
                <span className={styles.gardenButtonBrand}>
                  watch<span className={styles.reverent}>H</span>im.work
                </span>
              </span>
              <svg
                className={styles.gardenButtonChevron}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}

        {discovery && (
          <button
            type="button"
            className={styles.discoveryMoment}
            onClick={() => { openDetail(discovery.prayerId); setDiscovery(null); }}
          >
            {t('discovery.message', { who: discovery.who, months: discovery.months })}
          </button>
        )}
      </div>

      </div>

      {/* savedConfirm sits mid-screen at bottom: 45% of the container
          (a celebration overlay, not bottom chrome) so it stays a
          container-level sibling of the dock rather than nested inside
          it. Without this, its bottom: 45% would resolve against the
          dock's local coordinate system and land inside the dock zone. */}
      {status && (
        <div className={`${styles.savedConfirm} ${status === 'savedOffline' ? styles.savedConfirmOffline : ''}`.trim()}>
          {t(status === 'failed'
            ? 'prayScripture.saveFailed'
            : status === 'savedOffline' ? 'pray.savedOffline' : 'pray.saved')}
        </div>
      )}



      {gardenOpen && (
        <Garden
          zIndex={zGarden}
          prayers={prayers}
          onClose={closeGarden}
          onPrayerTap={openDetail}
          onOpenSummary={() => setSummaryOpen(true)}
          onOpenPerson={openPerson}
          onShareCard={(p) => openShareEntry(p.id)}
        />
      )}

      {shareCardPrayer && (
        <LetThemKnowSheet
          zIndex={zShareText}
          prayer={shareCardPrayer}
          onClose={closeShareFlow}
          authorizeOutbound={authorizeTextOutbound}
        />
      )}

      {sendChoiceCardPrayer && PICKER_ENABLED && (
        <ChoiceSheet
          zIndex={zShareChoice}
          onClose={closeShareFlow}
          onSendAsText={() => {
            // Additive: keep sendChoiceCardForId set so share-choice stays
            // mounted under share-text and back steps one screen.
            const id = sendChoiceCardForId;
            if (id && authorizeShare(id, 'outbound')) setShareCardForId(id);
          }}
          onMailACard={() => {
            const id = sendChoiceCardForId;
            if (id && authorizeShare(id, 'outbound')) setMailCardForId(id);
          }}
        />
      )}

      {mailCardPrayer && PICKER_ENABLED && (
        <MailCardSheet
          zIndex={zShareMail}
          prayer={mailCardPrayer}
          onClose={closeShareFlow}
          authorizeOutbound={authorizeMailOutbound}
          onContinue={(payload) => {
            console.info('[mailcard] redacted payload:', payload);
          }}
        />
      )}

      {/* S0 (PC-B rev 9): the sheet receives NO live prayer props — the
          authenticated server preview is the only prayer-bearing preview
          source. */}
      {/* Production opt-in gate: a deliberately disabled build mounts no
          keepsake sheet and the PersonView CTA below is absent under the same
          KEEPSAKES_ENABLED constant. */}
      {KEEPSAKES_ENABLED && keepsakeFor && (
        <PersonKeepsakeSheet
          zIndex={zKeepsake}
          who={keepsakeFor}
          onClose={() => setKeepsakeFor(null)}
        />
      )}

      {summaryOpen && (
        <RememberStream
          zIndex={zRemember}
          prayers={prayers}
          onClose={() => setSummaryOpen(false)}
          onNew={() => handleNew('praise')}
          // Do NOT close Remember when opening Detail — let Detail
          // layer above (z-index 110 > Remember 96) so back-from-Detail
          // returns to Remember with its scroll position preserved
          // (the DOM .scroll element keeps scrollTop while mounted).
          // Closing first would expose whatever's underneath (Garden
          // if Remember was opened from there), dumping the user out
          // of their reading position. Matches Garden's same pattern
          // at line ~958.
          onPrayerTap={openDetail}
          onShareMoment={handleShareMoment}
        />
      )}

      <ShareConfirmDialog
        zIndex={zShareConfirm}
        open={shareConfirmCardPrayer !== null}
        escapeEnabled={
          sendChoiceCardForId === null &&
          shareCardForId === null &&
          mailCardForId === null
        }
        onCancel={closeShareFlow}
        onConfirm={handleShareConfirmProceed}
      />

      <ShareConfirmDialog
        zIndex={zShareReconfirm}
        open={shareReconfirmCardPrayer !== null}
        onCancel={handleShareReconfirmCancel}
        onConfirm={handleShareReconfirmProceed}
      />

      {evidenceOpen && (
        <EvidenceBook
          zIndex={zEvidence}
          prayers={prayers}
          onClose={() => setEvidenceOpen(false)}
          onNew={() => handleNew() }
          onPrayerTap={openDetail}
          onOpenPerson={openPerson}
        />
      )}

      {detailPrayer && (
        <PrayerDetail
          zIndex={zDetail}
          prayer={detailPrayer}
          onClose={handleDetailClose}
          onDelete={handleDetailDelete}
          onNew={() => handleNew() }
          onPersonTap={openPerson}
          initialTestimony={
            detailInitialTestimony?.prayerId === detailPrayer.id ? detailInitialTestimony.id : null
          }
          onInitialTestimonyDone={(id) => {
            setDetailInitialTestimony((current) => (current?.id === id ? null : current));
          }}
        />
      )}

      {/* A3: standalone person overlay (above its opener). Timeline tap ->
          person-detail; keepsake CTA -> the existing keepsake layer. */}
      {personFor && (
        <PersonView
          zIndex={zPerson}
          who={personFor}
          prayers={prayers}
          onClose={closePerson}
          onOpenPersonDetail={setPersonDetailId}
          onOpenKeepsake={KEEPSAKES_ENABLED ? setKeepsakeFor : undefined}
        />
      )}

      {/* A3: person-scoped detail (a SECOND PrayerDetail, distinct `person-detail`
          key). NO onPersonTap -> the who-name is non-interactive here, capping
          person-nesting at one deep (README §3.4 round-11 B2). */}
      {personDetailPrayer && (
        <PrayerDetail
          zIndex={zPersonDetail}
          prayer={personDetailPrayer}
          onClose={personDetailClose}
          onDelete={personDetailDelete}
          onNew={() => handleNew() }
        />
      )}

      {/* Delete Undo + S0 protection messages (PC-B rev 9 locked keepsake.*
          copy). Rendered inside the surface the user is looking at (topmost
          dialog, else this container's HomeFeedbackArea slots) so overlays
          never hide them and they never overlap; see DeletionNotices. Info:
          tap dismisses; failure: its Dismiss button. */}
      <DeletionNotices
        homeRef={containerRef}
        homeSlots={undoSlot && headSlot && tailSlot ? { undo: undoSlot, head: headSlot, tail: tailSlot } : null}
        undo={recentlyDeleted ? undoMeta : null}
        info={infoNotice}
        failure={failureNotice}
        onUndo={handleUndoDelete}
        onDismissInfo={() => setInfoNotice(null)}
        onDismissFailure={() => setFailureNotice(null)}
        onUndoPausedChange={handleUndoPausedChange}
      />

      {/* B.2 lifecycle surface. Resume card portals to body so it
          lands above any open overlay; it is a no-op when there is no
          interrupted session. (The former queue-cap toast was removed
          2026-10-30: old recordings never block the mic.)

          Core-content cutover: the resume card is NOT rendered while
          read-only, and the gate is load-bearing rather than cosmetic.
          Backdrop and Escape are deliberately nondestructive; every actual
          resolution (Discard, Save what I have, Continue/Keep typing) still
          requires a write the active read-only gate rejects. Mounting the card here would
          therefore create a full-viewport modal at z-index 250 with no usable
          exit, on exactly the devices still holding orphan audio rows whose
          bytes may be the only copy of a prayer. Those devices are the ones
          the operator needs to be able to read during the verification pass. */}
      {!readOnly && (
        <ResumeRecordingCard
          renderEntryChoice={(queueItemId) => <EntryModeChoice value={recoveryModes[queueItemId] ?? null}
            onChoose={mode => setRecoveryModes(current => ({ ...current, [queueItemId]: mode }))} />}
          entryChoiceMade={queueItemId => Boolean(recoveryModes[queueItemId])}
          onContinueEnhanced={handleContinueEnhanced}
          onRecordingRemoved={handleRecordingRemoved}
          onResumeEnhanced={handleResumeEnhanced}
          onSavePending={handleSavePending}
          onKeepTyping={handleKeepTyping}
        />
      )}
    </div>
  );
}
